/**
 * PDFExportModal Component
 * 
 * This component provides functionality to export player data to PDF with various options.
 * Updated to use the same PDF generation logic as Players.jsx for consistency.
 */

import React, { useState, useRef, useEffect } from 'react';
import './PDFExportModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilePdf,
  faTimes,
  faCheck,
  faToggleOn,
  faToggleOff,
  faDownload,
  faUsers,
  faSort,
  faLayerGroup,
  faUser,
  faPalette,
  faImage,
  faShieldAlt,
  faStar,
  faUserTie,
  faAddressCard
} from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from "jspdf";
import { toast } from 'react-toastify';
import { 
  bgtop1, 
  toplprofile, 
  ribbin, 
  ribbin3,
  rankbadge, 
  topborderirng, 
  plbg, 
  tagbadge, 
  pdfauctiontitlebg,
  auctiondefaultLogo,
  teamdefaultLogo,
  playerdefaultLogo,
  pdflayout1,
  pdflayout2,
  whitebg,
  pdfbg 
} from '../../constants/Images';

// Default images to use as fallbacks
const defaultPlayerImg = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";
const defaultLogoImg = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";

const PDFExportModal = ({ isOpen, onClose, data, categories, teams, teamPath, currentAuction, playerImages, auctionLogo, sponsorLogo, backgroundImage }) => {
  const [pdfType, setPdfType] = useState('normal'); // normal, category, team, player, top, owners
  const [pdfLayout, setPdfLayout] = useState('1'); // 1, 2
  const [playerFilter, setPlayerFilter] = useState('1'); // Using values from Player.jsx
  const [orientation, setOrientation] = useState('portrait'); // portrait, landscape
  const [showBackground, setShowBackground] = useState(true);
  const [orderBy, setOrderBy] = useState('');
  const [columnCount, setColumnCount] = useState('3'); // Default to 2 columns matching UI
  const [perPagePlayers, setPerPagePlayers] = useState('24'); // Default to 14 players for 2-column layout
  const [isPlNoShow, setIsPlNoShow] = useState(true);
  const [isPointsShow, setIsPointsShow] = useState(true);
  const [isCatShow, setIsCatShow] = useState(true);
  const [isSpnShow, setIsSpnShow] = useState(false);
  const [isNoCatShow, setIsNoCatShow] = useState(false);
  const [isMobNoShow, setIsMobNoShow] = useState(false);
  const [isTshirtShow, setIsTshirtShow] = useState(false);
  const [isBasePriceShow, setIsBasePriceShow] = useState(false);
  const [isValShow, setIsValShow] = useState(true);
  const [isSpec1, setIsSpec1] = useState(true);
  const [isSpec2, setIsSpec2] = useState(true);
  const [isSpec3, setIsSpec3] = useState(true);
  const [isOwnerShow, setIsOwnerShow] = useState(false);
  const [isDetailShow, setIsDetailShow] = useState(false);
  const [ispdfwhitebg, setIspdfwhitebg] = useState(localStorage.getItem('ispdfwhitebg') || '0');
  const [fontcolor, setFontcolor] = useState(localStorage.getItem('pdffont') || '0');
  
  // Additional state variables for new PDF types
  const [toppltype, setToppltype] = useState('1'); // '1' = All, '2' = Category wise
  const [topnum, setTopnum] = useState('5'); // Top number selection
  const [toptype, setToptype] = useState('0'); // '0' = Single page, '1' = Multiple pages
  const [ignoretype, setIgnoretype] = useState(''); // '', 'retain', 'cat', 'tag', 'plno'
  const [ignorecat, setIgnorecat] = useState('');
  const [ignoretag, setIgnoretag] = useState('');
  const [ignoreplnos, setIgnoreplnos] = useState('');
  const [generatecatid, setGeneratecatid] = useState('');
  
  // Owner tag selection states for Owners PDF
  const [isownertagselect, setIsownertagselect] = useState(true);
  const [iscoownertagselect, setIscoownertagselect] = useState(true);
  const [iscaptaintagselect, setIscaptaintagselect] = useState(true);
  const [isvicecaptaintagselect, setIsvicecaptaintagselect] = useState(true);
  const [isicontagselect, setIsicontagselect] = useState(true);
  const [isretaintagselect, setIsretaintagselect] = useState(true);

  // Image refs and base64 state for logo conversion (similar to Players.jsx)
  const aucImageRefs = useRef({});
  const [aucImageBase64, setAucImageBase64] = useState({});
  
  // Round player images state for special PDFs
  const [roundPlayerImages, setRoundPlayerImages] = useState({});
  
  // Function to convert image to base64 with round option (from Players.jsx)
  const convertImageToBase64 = async (imageUrl, options = {}) => {
    const {
      maxWidth = 800, // Maximum width for regular image
      maxHeight = 800, // Maximum height for regular image
      quality = 0.7, // JPEG quality (0-1)
      roundedSize = 300, // Size for the rounded/circular image
      roundedQuality = 0.7 // Quality for rounded image
    } = options;
  
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const img = await createImageBitmap(blob);
  
      const base64 = {};
      
      // Create optimized regular image
      const regularCanvas = document.createElement('canvas');
      const regularCtx = regularCanvas.getContext('2d');
      
      // Calculate dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth) {
        height = Math.floor(height * (maxWidth / width));
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = Math.floor(width * (maxHeight / height));
        height = maxHeight;
      }
      
      regularCanvas.width = width;
      regularCanvas.height = height;
      regularCtx.drawImage(img, 0, 0, width, height);
      
      // Get compressed regular image
      base64.regular = regularCanvas.toDataURL('image/jpeg', quality);
      
      // Create optimized rounded image using the same approach as original Players.jsx
      // This ensures the full image is visible, just clipped to circular shape
      const maxDimension = Math.max(img.width, img.height);
      const size = Math.min(roundedSize, maxDimension);
      const roundedCanvas = document.createElement('canvas');
      roundedCanvas.width = size;
      roundedCanvas.height = size;
  
      const roundedCtx = roundedCanvas.getContext('2d');
  
      // Draw circular clipping path
      roundedCtx.beginPath();
      roundedCtx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2, true);
      roundedCtx.closePath();
      roundedCtx.clip();
  
      // Scale the image to fit within the circular canvas while maintaining aspect ratio
      const scale = size / maxDimension;
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      
      // Center the scaled image within the canvas
      const offsetX = (size - scaledWidth) / 2;
      const offsetY = (size - scaledHeight) / 2;
      
      // Draw the entire image scaled to fit within the circular area
      roundedCtx.drawImage(
        img,
        0,
        0,
        img.width,
        img.height,
        offsetX,
        offsetY,
        scaledWidth,
        scaledHeight
      );
  
      // Get compressed rounded image
      base64.rounded = roundedCanvas.toDataURL('image/png', roundedQuality);
      
      return base64;
    } catch (error) {
      console.error("Error converting image to base64:", error);
      return null;
    } 
  };

  // Load player round images for special PDFs and create optimized versions for multiple pages
  const loadRoundPlayerImages = async () => {
    if (!data?.data?.list || !playerImages) return;
    
    const roundImages = {};
    const playerList = data.data.list;
    
    try {
      for (const player of playerList) {
        if (playerImages[player.pid] && playerImages[player.pid] !== playerdefaultLogo) {
          try {
            // Convert regular image to round using the same parameters as Players.jsx for consistency
            const base64Result = await convertImageToBase64(playerImages[player.pid], {
              maxWidth: 800, // Same as Players.jsx
              maxHeight: 800,
              quality: 0.7, // Same as Players.jsx
              roundedSize: 300, // Same as Players.jsx - this is key for proper cropping
              roundedQuality: 0.7 // Same as Players.jsx
            });
            if (base64Result && base64Result.rounded) {
              roundImages[player.pid] = base64Result.rounded;
            }
          } catch (error) {
            console.error(`Error creating round image for player ${player.pid}:`, error);
          }
        }
      }
      
      setRoundPlayerImages(roundImages);
    } catch (error) {
      console.error("Error loading round player images:", error);
    }
  };

  // Load images when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset base64 images when modal opens
      setAucImageBase64({});
      setRoundPlayerImages({});
      
      // Load round player images for special PDFs
      if (playerImages) {
        loadRoundPlayerImages();
      }
    }
  }, [isOpen, playerImages]);

  const getPlayerCount = () => {
    if (!data?.data?.list) return 0;
    
    let filteredPlayers = [...data.data.list];
    
    switch (playerFilter) {
      case '2': // Available
        filteredPlayers = filteredPlayers.filter(player => 
          (player.tmid === '0' || player.tmid === "") && player.isskiped !== '1');
        break;
      case '3': // Sold
        filteredPlayers = filteredPlayers.filter(player => player.tmid > 0);
        break;
      case '4': // Unsold
        filteredPlayers = filteredPlayers.filter(player => player.isskiped === '1');
        break;
      case '5': // Unsold + available
      case '6': // Available + unsold (same logic)
        filteredPlayers = filteredPlayers.filter(player => 
          player.isskiped === '1' || (player.tmid === '0' || player.tmid === ""));
        break;
      case '1': // ALL
      default:
        // No filtering, keep all players
        break;
    }
    
    return filteredPlayers.length;
  };

  // Reset orderBy when PDF type changes
  const handlePdfTypeChange = (type) => {
    setPdfType(type);
    setOrderBy('');
  };

  // Function to convert image to base64 format with optimization for PDFs
  const convertaucteamtobase64 = (img, index) => {
    console.log(`Converting image to base64 for index: ${index}`);
    if (!img) {
      console.log(`No image provided for index: ${index}`);
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Optimize image size for PDFs - smaller dimensions for better file size
    const maxSize = 200; // Maximum dimension
    let { width, height } = img;
    
    if (width > maxSize || height > maxSize) {
      if (width > height) {
        height = Math.floor(height * (maxSize / width));
        width = maxSize;
      } else {
        width = Math.floor(width * (maxSize / height));
        height = maxSize;
      }
    }
    
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    // Use lower quality for smaller file size in PDFs
    const quality = index === '2' ? 0.5 : 0.6; // Background gets even lower quality
    const base64Data2 = canvas.toDataURL('image/jpeg', quality);
    console.log(`Base64 conversion successful for index: ${index}, size: ${width}x${height}, length: ${base64Data2.length}`);

    if (base64Data2) {
      const newarr2 = { ...aucImageBase64 };
      newarr2[index] = base64Data2;
      setAucImageBase64(newarr2);
      console.log(`Updated aucImageBase64 for index: ${index}`);
    }
  };

  // Handle image load event (similar to Players.jsx)
  const handleaucimgload = (index) => {
    return () => {
      if (aucImageRefs.current[index]) {
        convertaucteamtobase64(aucImageRefs.current[index], index);
      }
    };
  };

  // Function to handle line wrapping for text in PDF
  function linechanger(str, count) {
    if (!str) return [''];
    
    var plsp = str.split(" ")
    let plsplen = 0;
    let whichsp = 0;
    let firststr = "";
    let secstr = "";
    for (let i = 0; i < plsp.length; i++) {
      plsplen += plsp[i].length;

      if (plsplen > count) {
        break;
      }
      whichsp += 1
    }
    var linearr = []
    for (let y = 0; y < whichsp; y++) {
      if (firststr !== '') {
        firststr += " "
      }
      firststr += plsp[y];
    }
    linearr.push(firststr)

    for (let z = whichsp; z < plsp.length; z++) {
      if (secstr !== '') {
        secstr += " "
      }
      secstr += plsp[z];
    }
    linearr.push(secstr)
    return linearr
  }

  // Helper functions for Tag-wise PDF (from Players.jsx)
  const getTagName = (ownertag) => {
    switch (ownertag) {
      case '1': return 'Owner';
      case '2': return 'Co-owner';
      case '3': return 'Captain';
      case '4': return 'Icon';
      case '5': return 'Retain';
      case '6': return 'V.Captain';
      default: return '';
    }
  };

  const calculatePlayerStartX = (playerCount, baseX, istwolane) => {
    if (istwolane) {
      return baseX - (playerCount === 1 ? 0 : 20);
    }

    // Adjust spacing based on player count
    let spacing;
    if (playerCount >= 5) {
      spacing = 30; // Reduced spacing for 5 or more players
    } else if (playerCount === 4) {
      spacing = 33; // Medium spacing for 4 players
    } else {
      spacing = 36; // Original spacing for 3 or fewer players
    }

    // Center players based on count and adjusted spacing
    const totalWidth = (playerCount - 1) * spacing;
    return baseX - (totalWidth / 2);
  };

  const drawPlayer = (doc, player, x, y, imageSize, playerCount) => {
    const radius = imageSize / 2 + 3;
    const adjustedImageSize = playerCount >= 5 ? imageSize * 0.9 : imageSize;
    const adjustedRadius = adjustedImageSize / 2 + 3;

    // Draw circles
    doc.setDrawColor(165, 148, 247);
    doc.setLineWidth(1);
    doc.circle(x, y, adjustedImageSize / 2 + 0.2, 'S');
    doc.setDrawColor(170, 180, 227);
    doc.setLineWidth(0.3);
    doc.circle(x, y, adjustedImageSize / 2 + 1.8, 'S');

    // Add player image (use round image if available)
    if (roundPlayerImages[player.pid]) {
      try {
        doc.addImage(roundPlayerImages[player.pid], 'png', x - adjustedImageSize / 2, y - adjustedImageSize / 2, adjustedImageSize, adjustedImageSize);
      } catch (e) {
        console.error('Error adding round player image:', e);
        // Fallback to regular image
        if (playerImages && playerImages[player.pid]) {
          try {
            doc.addImage(playerImages[player.pid], 'jpeg', x - adjustedImageSize / 2, y - adjustedImageSize / 2, adjustedImageSize, adjustedImageSize);
          } catch (e2) {
            // Use default if both fail
          }
        }
      }
    } else if (playerImages && playerImages[player.pid]) {
      try {
        doc.addImage(playerImages[player.pid], 'jpeg', x - adjustedImageSize / 2, y - adjustedImageSize / 2, adjustedImageSize, adjustedImageSize);
      } catch (e) {
        // Use default if image fails
      }
    }

    // Add tag badge
    const badgeWidth = adjustedImageSize * 1.5;
    const badgeHeight = adjustedImageSize * 0.3;
    doc.addImage(tagbadge, 'PNG', x - badgeWidth / 2, y + adjustedImageSize / 2 - badgeHeight / 2, badgeWidth, badgeHeight);

    // Draw player name in arc
    drawArcText(doc, player.pname, x, y, adjustedRadius);

    // Add tag name
    const tagname = getTagName(player.ownertag);
    doc.setFont("Courier", "bold");
    doc.setFontSize(playerCount >= 5 ? 6 : 7);
    doc.text(tagname, x - (tagname.length / 2) * 1.4, y + (adjustedRadius - 2.7));
  };

  const drawArcText = (doc, text, centerX, centerY, radius) => {
    // Text settings
    doc.setFontSize(8);
    if (fontcolor == 0) {
      doc.setTextColor('#ffffff');
    } else {
      doc.setTextColor('#141C5C'); // Dark blue for white background
    }
    doc.setFont("Courier", "normal");

    // Calculate text positioning
    const tlength = text.length / 2;
    const angleOffset = -(Math.PI / 2) * ((tlength + 1) / 14);
    const falselength = 26;

    // Draw each character along the arc
    for (let i = 0; i < text.length; i++) {
      const rta = (tlength - 0.5 - i) * 7;
      const char = text[i];
      const angle = angleOffset + (i - (falselength - 1) / 2) * (Math.PI / falselength);
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      doc.text(char, x, y, null, rta);
    }
  };

  const addPageHeaderForSpecialPDF = (doc, subtitle = null) => {
    // Add background and logos - prioritize white background when toggled
    if (ispdfwhitebg === "1") {
      doc.addImage(whitebg, 'JPEG', 0, 0, 210, 297);
    } else if (aucImageBase64['2']) {
      doc.addImage(aucImageBase64['2'], 'JPEG', 0, 0, 210, 297);
    } else {
      doc.addImage(pdfbg, 'JPEG', 0, 0, 210, 297);
    }
    doc.addImage(pdfauctiontitlebg, 'JPEG', 35, 10, 135, 20);

    if (aucImageBase64['0']) {
      doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 20, 20);
      doc.addImage(aucImageBase64['0'], 'JPEG', 175, 10, 20, 20);
    } else {
      doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 20, 20);
      doc.addImage(defaultLogoImg, 'JPEG', 175, 10, 20, 20);
    }

    // Add auction name
    if (fontcolor == 0) {
      doc.setTextColor(255, 255, 255);
    } else {
      doc.setTextColor(20, 14, 92);
    }
    const auctionName = currentAuction?.name || "Auction";
    if (auctionName.length < 27) {
      const alen = auctionName.length * 3.5;
      doc.text(auctionName.toUpperCase(), 105 - alen / 2, 22);
    } else {
      doc.setFontSize(15);
      var fstr = linechanger(auctionName, 27);
      let alen1 = fstr[0].length * 3.2;
      doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 20);
      if (fstr[1].length <= 25) {
        doc.setFontSize(11);
        let alen2 = fstr[1].length * 2.9;
        doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 24.5);
      } else {
        var fsstr = linechanger(fstr[1], 25);
        doc.setFontSize(14);
        let alen2 = fsstr[0].length * 2.9;
        let alen3 = fsstr[1].length * 2.9;
        doc.text(fsstr[0].toUpperCase(), 105 - alen2 / 2, 17);
        doc.text(fsstr[1].toUpperCase(), 105 - alen3 / 2, 24);
      }
    }
    
    if (subtitle) {
      doc.setFontSize(12);
      if (fontcolor == 0) {
        doc.setTextColor(196, 147, 14);
      } else {
        doc.setTextColor(22, 14, 117);
      }
      const sublen = subtitle.length * 2;
      doc.text(subtitle.toUpperCase(), 105 - sublen / 2, 35);
    }
  };

  // Circular layout PDF generation (Type 2) - similar to Players.jsx pdfdownload2
  const generateCircularLayoutPDF = (pdfdata) => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Add background - prioritize white background when toggled
    if (ispdfwhitebg === "1") {
      doc.addImage(whitebg, 'JPEG', 0, 0, 210, 297);
    } else if (aucImageBase64['2']) {
      doc.addImage(aucImageBase64['2'], 'JPEG', 0, 0, 210, 297);
    } else {
      doc.addImage(pdfbg, 'JPEG', 0, 0, 210, 297);
    }
    
    // Add title background
    doc.addImage(pdfauctiontitlebg, 'JPEG', 35, 10, 135, 20);
    
    // Add logos
    if (aucImageBase64['0']) {
      if (pdfType !== 'team') {
        doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 20, 20);
      }
      
      if (isSpnShow && aucImageBase64['1']) {
        doc.addImage(aucImageBase64['1'], 'JPEG', 175, 10, 20, 20);
      } else {
        doc.addImage(aucImageBase64['0'], 'JPEG', 175, 10, 20, 20);
      }
    } else {
      doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 20, 20);
      doc.addImage(defaultLogoImg, 'JPEG', 175, 10, 20, 20);
    }
    
    // For team-wise PDFs, add team logo on left
    if (pdfType === 'team' && pdfdata.length > 0 && teams) {
      const firstPlayerTeam = teams.find(team => team.tid == pdfdata[0].tmid);
      if (firstPlayerTeam && aucImageBase64[firstPlayerTeam.tid]) {
        doc.addImage(aucImageBase64[firstPlayerTeam.tid], 'JPEG', 10, 10, 20, 20);
      }
    }
    
    // Add auction name
    if (fontcolor == 0) {
      doc.setTextColor(255, 255, 255);
    } else {
      doc.setTextColor(20, 14, 92);
    }
    const auctionName = currentAuction?.name || "Auction";
    if (auctionName.length < 27) {
      let alen = auctionName.length * 3.5;
      doc.text(auctionName.toUpperCase(), 105 - alen / 2, 22);
    } else {
      doc.setFontSize(15);
      var fstr = linechanger(auctionName, 27);
      let alen1 = fstr[0].length * 3.2;
      doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 20);
      if (fstr[1] && fstr[1].length <= 25) {
        doc.setFontSize(14);
        let alen2 = fstr[1].length * 2.9;
        doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 25);
      } else {
        var fsstr = linechanger(fstr[1], 25);
        doc.setFontSize(14);
        let alen2 = fsstr[0].length * 2.9;
        let alen3 = fsstr[1].length * 2.9;
        doc.text(fsstr[0].toUpperCase(), 105 - alen2 / 2, 17);
        doc.text(fsstr[1].toUpperCase(), 105 - alen3 / 2, 24);
      }
    }
    
    // Circular layout parameters
    let centerX = 30;
    let centerY = 60;
    let imageSize = 20;
    let radius = imageSize / 2 + 3;
    let adjustCenter = imageSize / 2;
    
    // Track category/team changes
    var dumcatid = pdfdata.length > 0 ? pdfdata[0].catid : "";
    var dumtid = pdfdata.length > 0 ? pdfdata[0].tmid : "";
    let count = 0;
    let counter = 0;
    
    pdfdata.forEach((player, index) => {
      if (count == 0) {
        if (pdfType === 'category') {
          var catname = categories?.find(ct => ct.cid === player.catid)?.cname || "Category";
          let clen = catname.length * 2;
          doc.text(catname.toUpperCase(), 98 - clen / 2, 50);
          centerY = 70;
        }
        if (pdfType === 'team') {
          var teamname = teams?.find(tm => tm.tid == player.tmid)?.name || "Team";
          const teamText = teamname.toUpperCase();
          const teamTextWidth = doc.getTextWidth(teamText);
          doc.text(teamText, (doc.internal.pageSize.getWidth() - teamTextWidth) / 2, 35);
        }
      }
      
      // Handle category page breaks
      if (pdfType === 'category') {
        if (dumcatid !== player.catid) {
          dumcatid = player.catid;
          counter = 0;
          count = 0;
          doc.addPage();
          doc.setFontSize(16);
          
          var catname = categories?.find(ct => ct.cid === player.catid)?.cname || "Category";
          
          // Add background and logos for new page - prioritize white background when toggled
          if (ispdfwhitebg === "1") {
            doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          } else if (aucImageBase64['2']) {
            doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          } else {
            doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          }
          doc.addImage(pdfauctiontitlebg, 'JPEG', 35, 10, 135, 20);
          
          if (aucImageBase64['0']) {
            doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 20, 20);
            if (isSpnShow && aucImageBase64['1']) {
              doc.addImage(aucImageBase64['1'], 'JPEG', 175, 10, 20, 20);
            } else {
              doc.addImage(aucImageBase64['0'], 'JPEG', 175, 10, 20, 20);
            }
          } else {
            doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 20, 20);
            doc.addImage(defaultLogoImg, 'JPEG', 175, 10, 20, 20);
          }
          
          // Add auction name
          if (fontcolor == 0) {
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setTextColor(20, 14, 92);
          }
          if (auctionName.length < 27) {
            let alen = auctionName.length * 4;
            doc.text(auctionName.toUpperCase(), 105 - alen / 2, 20);
          } else {
            doc.setFontSize(15);
            var fstr = linechanger(auctionName, 27);
            let alen1 = fstr[0].length * 3.2;
            doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 20);
            if (fstr[1] && fstr[1].length <= 25) {
              doc.setFontSize(14);
              let alen2 = fstr[1].length * 2.9;
              doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 26);
            }
          }
          
          doc.setFontSize(16);
          let clen = catname.length * 2;
          doc.text(catname.toUpperCase(), 95 - clen / 2, 35);
          centerX = 30;
          centerY = 70;
        }
      }
      
      // Handle team page breaks
      if (pdfType === 'team') {
        if (dumtid !== player.tmid) {
          dumtid = player.tmid;
          counter = 0;
          count = 0;
          doc.addPage();
          doc.setFontSize(16);
          
          var teamname = teams?.find(tm => tm.tid == player.tmid)?.name || "Team";
          
          // Add background and logos for new page - prioritize white background when toggled
          if (ispdfwhitebg === "1") {
            doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          } else if (aucImageBase64['2']) {
            doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          } else {
            doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
          }
          doc.addImage(pdfauctiontitlebg, 'JPEG', 35, 10, 135, 20);
          
          if (aucImageBase64['0']) {
            if (isSpnShow && aucImageBase64['1']) {
              doc.addImage(aucImageBase64['1'], 'JPEG', 175, 10, 20, 20);
            } else {
              doc.addImage(aucImageBase64['0'], 'JPEG', 175, 10, 20, 20);
            }
          } else {
            doc.addImage(defaultLogoImg, 'JPEG', 175, 10, 20, 20);
          }
          
          // Add team logo
          const currentTeam = teams?.find(tm => tm.tid == player.tmid);
          if (currentTeam && aucImageBase64[currentTeam.tid]) {
            doc.addImage(aucImageBase64[currentTeam.tid], 'JPEG', 10, 10, 20, 20);
          } else {
            doc.addImage(teamdefaultLogo, 'JPEG', 10, 10, 20, 20);
          }
          
          // Add auction name
          if (fontcolor == 0) {
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setTextColor(20, 14, 92);
          }
          doc.setFontSize(18);
          if (auctionName.length < 27) {
            let alen = auctionName.length * 4;
            doc.text(auctionName.toUpperCase(), 105 - alen / 2, 20);
          } else {
            doc.setFontSize(15);
            var fstr = linechanger(auctionName, 27);
            let alen1 = fstr[0].length * 3.2;
            doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 20);
            if (fstr[1] && fstr[1].length <= 25) {
              doc.setFontSize(14);
              let alen2 = fstr[1].length * 2.9;
              doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 26);
            }
          }
          
          doc.setFontSize(16);
          const teamTextWidth = doc.getTextWidth(teamname);
          doc.text(teamname, (doc.internal.pageSize.getWidth() - teamTextWidth) / 2, 35);
          centerX = 30;
          centerY = 70;
        }
      }
      
      // Draw border circles
      doc.setDrawColor(165, 148, 247);
      doc.setLineWidth(1);
      doc.circle(centerX, centerY, imageSize / 2 + 0.2, 'S');
      doc.setDrawColor(170, 180, 227);
      doc.setLineWidth(0.3);
      doc.circle(centerX, centerY, imageSize / 2 + 1.8, 'S');
      
      // Add player image (use round image if available)
      if (roundPlayerImages[player.pid]) {
        try {
          doc.addImage(roundPlayerImages[player.pid], 'jpeg', centerX - adjustCenter, centerY - adjustCenter, imageSize, imageSize);
        } catch (e) {
          console.error('Error adding round player image in circular layout:', e);
        }
      }
      
      // Draw player name curved around the circle
      const text = player.pname;
      let tlength = text.length / 2;
      const angleOffset = -(Math.PI / 2) * ((tlength + 1) / 14);
      let falselength = 26;
      
      doc.setFontSize(8);
      doc.setTextColor('#ffffff');
      doc.setFont("Courier", "normal");
      
      for (let i = 0; i < text.length; i++) {
        let rta = (tlength - 0.5 - i) * 7;
        const char = text[i];
        const angle = angleOffset + (i - (falselength - 1) / 2) * (Math.PI / falselength);
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        doc.text(char, x, y, null, rta);
      }
      
      // Add player number
      doc.setFont("Courier", "bold");
      doc.setFontSize(6);
      doc.text(String(index + 1), centerX - (radius + 3), centerY);
      doc.setTextColor('#ffffff');
      
      // Update position for next player
      centerX += 36;
      
      // Move to next row after 5 players
      if ((counter + 1) % 5 == 0) {
        centerY += 30;
        centerX = 30;
      }
      
      // Add new page after 40 players
      if ((counter + 1) % 40 == 0) {
        doc.addPage();
        // Add background - prioritize white background when toggled
        if (ispdfwhitebg === "1") {
          doc.addImage(whitebg, 'JPEG', 0, 0, 210, 297);
        } else if (aucImageBase64['2']) {
          doc.addImage(aucImageBase64['2'], 'JPEG', 0, 0, 210, 297);
        } else {
          doc.addImage(pdfbg, 'JPEG', 0, 0, 210, 297);
        }
        centerY = 50;
        centerX = 30;
      }
      
      count += 1;
      counter += 1;
    });
    
    return doc;
  };
  
  // Tag-wise PDF generation (Owners)
  const generateTagwisePDF = () => {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Prepare data
    var tagwisepdf = [];
    var tagplayercount = [];

    // Group players by team and filter by tags
    teams.forEach((tm) => {
      var teamwise = data.data.list.filter((dt) => dt.isowner == tm.tid || dt.tmid == tm.tid);
      var teamwisefiltered = [];

      // Collect all tagged players based on selections
      if (isownertagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '1')];
      }
      if (iscoownertagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '2')];
      }
      if (iscaptaintagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '3')];
      }
      if (isicontagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '4')];
      }
      if (isretaintagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '5')];
      }
      if (isvicecaptaintagselect) {
        teamwisefiltered = [...teamwisefiltered, ...teamwise.filter((ele) => ele.ownertag == '6')];
      }
      
      // Remove duplicates
      teamwisefiltered = teamwisefiltered.filter((player, index, self) => 
        index === self.findIndex(p => p.pid === player.pid)
      );
      
      // Sort players by pcode
      teamwisefiltered.sort((a, b) => parseInt(a.pcode) - parseInt(b.pcode));

      if (teamwisefiltered.length > 0) {
        tagplayercount.push(teamwisefiltered.length);
        tagwisepdf.push({
          team: tm,
          players: teamwisefiltered
        });
      }
    });

    if (tagwisepdf.length === 0) {
      toast.warning('No tagged players found for selected teams');
      return;
    }

    // Determine layout mode
    const maxPlayersPerTeam = Math.max(...tagplayercount);
    const istwolane = maxPlayersPerTeam <= 2;

    // PDF generation constants
    var teamsPerPage = istwolane ? 10 : 6;
    const imageSize = istwolane ? 23 : 17;
    const spacing = istwolane ? 40 : 36;

    let pageCount = 0;

    // Process teams in groups
    for (let teamIndex = 0; teamIndex < tagwisepdf.length; teamIndex++) {
      // Add new page if needed
      if (teamIndex % teamsPerPage === 0) {
        if (teamIndex > 0) {
          doc.addPage();
        }
        addPageHeaderForSpecialPDF(doc, 'TAG LIST');
        pageCount++;
      }

      const teamData = tagwisepdf[teamIndex];
      const pagePosition = teamIndex % teamsPerPage;

      // Calculate team position
      let baseY = 70 + Math.floor(pagePosition / (istwolane ? 2 : 1)) * (istwolane ? 48 : 41);
      let baseX = istwolane ?
        (pagePosition % 2 === 0 ? 60 : 140) :
        98;

      // Draw team name
      doc.setFontSize(12);
      doc.setTextColor(237, 215, 12);
      doc.setFont('helvetica', 'bold');
      const teamName = teamData.team.name.toUpperCase();
      const teamNameWidth = teamName.length * 2;
      doc.text(teamName, baseX - teamNameWidth / 2, baseY - 20);

      // Calculate starting X for players based on count
      let playerBaseX = calculatePlayerStartX(teamData.players.length, baseX, istwolane);

      // Draw players
      teamData.players.forEach((player, playerIndex) => {
        const playerX = playerBaseX + (playerIndex * (teamData.players.length >= 5 ? 30 : teamData.players.length === 4 ? 33 : 36));
        drawPlayer(doc, player, playerX, baseY, imageSize, teamData.players.length);
      });
    }

    const auctionId = currentAuction?.aid || "auction";
    const auctionNameForFilename = (currentAuction?.name || "Auction").toUpperCase().replace(/[^a-z0-9]/gi, '_');
    doc.save(`${auctionId}-${auctionNameForFilename}_TAGLIST.pdf`);
  };

  // Top players PDF generation
  const generateTopPlayersPDF = () => {
    const doc = new jsPDF();
    let playersData = [...data.data.list];
    
    // Handle category-wise top players
    if (toppltype === '2' && categories && categories.length > 0) {
      var catwisefull = [];
      categories.forEach((ct) => {
        var catwise = data.data.list.filter((dt) => dt.catid == ct.cid);
        if (catwise.length > 0) {
          catwise = [...catwise].sort((a, b) => b.points - a.points);
          catwise = catwise.slice(0, parseInt(topnum));
          catwise.forEach((ele) => {
            catwisefull.push(ele);
          });
        }
      });
      playersData = catwisefull;
    }
    
    // Filter players based on ignore settings
    let soldplayers = [];
    if (ignoretype == '') {
      soldplayers = playersData.filter((ele) => ele.tmid > 0);
    } else if (ignoretype == 'retain') {
      soldplayers = playersData.filter((ele) => ele.tmid > 0 && ele.isfreez !== '1');
    } else if (ignoretype == 'cat') {
      soldplayers = playersData.filter((ele) => ele.tmid > 0 && ele.catid !== String(ignorecat));
    } else if (ignoretype == 'tag') {
      soldplayers = playersData.filter((ele) => ele.tmid > 0 && ele.ownertag !== ignoretag);
    } else if (ignoretype == 'plno') {
      let igids = ignoreplnos.split(',');
      soldplayers = playersData.filter((ele) => ele.tmid > 0 && !igids.includes(ele.pcode));
    }
    
    // Get top players
    var topplayers = [];
    if (toppltype !== '2') {
      const sortedData = [...soldplayers].sort((a, b) => b.points - a.points);
      topplayers = sortedData.slice(0, parseInt(topnum));
    } else {
      topplayers = soldplayers;
    }
    
    if (topplayers.length == 0 || topplayers.length < parseInt(topnum)) {
      toast.warning(`Cannot find ${topnum} sold players with current filters`);
      return;
    }

    // Multiple pages layout - OPTIMIZED VERSION
    if (toptype !== '0' || parseInt(topnum) == 1) {
      topplayers.forEach((ele, i) => {
        // Create a clean, minimal layout without heavy decorative images
        
        // Simple background color instead of heavy image
        doc.setFillColor(27, 24, 66); // Dark blue background
        doc.rect(0, 0, doc.internal.pageSize.width, doc.internal.pageSize.height, 'F');
        
        // Add minimal header decoration
        doc.setFillColor(232, 192, 84); // Gold color
        doc.rect(20, 10, doc.internal.pageSize.width - 40, 40, 'F');
        
        // Add small logos only if available and optimized
        if (aucImageBase64['0']) {
          try {
            doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 30, 30);
            doc.addImage(aucImageBase64['0'], 'JPEG', 170, 10, 30, 30);
          } catch (e) {
            // Skip logos on error
          }
        }
        
        // Add auction name with simple styling
        doc.setTextColor(27, 24, 66); // Dark text on gold background
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        const auctionName = currentAuction?.name || "Auction";
        if (auctionName.length < 35) {
          let alen = auctionName.length * 2.8;
          doc.text(auctionName.toUpperCase(), 105 - alen / 2, 25);
        } else {
          doc.setFontSize(14);
          var fstr = linechanger(auctionName, 35);
          let alen1 = fstr[0].length * 2.5;
          doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 22);
          if (fstr[1] && fstr[1].length > 0) {
            let alen2 = fstr[1].length * 2.5;
            doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 30);
          }
        }
        
        // Add "TOP PLAYERS" text
        doc.setTextColor(232, 192, 84);
        doc.setFontSize(12);
        doc.text('TOP PLAYERS', 88, 40);
        
        // Simple player card background
        doc.setFillColor(255, 255, 255); // White background for player card
        doc.roundedRect(50, 70, 110, 140, 5, 5, 'F');
        
        // Add player rank in a simple circle
        doc.setFillColor(232, 192, 84);
        doc.circle(105, 90, 15, 'F');
        doc.setTextColor(27, 24, 66);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(20);
        doc.text(String(i + 1), 105 - (String(i + 1).length * 3), 95);
        
        // Add optimized player image in a smaller size
        if (roundPlayerImages[ele.pid]) {
          try {
            doc.addImage(roundPlayerImages[ele.pid], 'PNG', 80, 110, 50, 50);
          } catch (e) {
            console.error('Error adding round player image in multiple pages:', e);
            // Simple placeholder rectangle instead of image
            doc.setFillColor(200, 200, 200);
            doc.rect(80, 110, 50, 50, 'F');
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(10);
            doc.text('No Image', 105 - 15, 138);
          }
        } else {
          // Simple placeholder rectangle instead of default logo
          doc.setFillColor(200, 200, 200);
          doc.rect(80, 110, 50, 50, 'F');
          doc.setTextColor(100, 100, 100);
          doc.setFontSize(10);
          doc.text('No Image', 105 - 15, 138);
        }
        
        // Player name
        doc.setTextColor(27, 24, 66);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        let pnlen = ele.pname.length * 2;
        doc.text(ele.pname.toUpperCase(), 105 - pnlen / 2, 175);
        
        // Player points
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        var pointsText = String(parseFloat(ele.points)) + ' POINTS';
        var pointsLen = pointsText.length * 1.8;
        doc.text(pointsText, 105 - pointsLen / 2, 185);
        
        // Team name if available
        if (teams && teams.length > 0 && ele.tmid > 0) {
          var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
          if (teamname) {
            doc.setFontSize(10);
            var teamText = 'TEAM: ' + teamname.toUpperCase();
            var teamLen = teamText.length * 1.5;
            doc.text(teamText, 105 - teamLen / 2, 195);
          }
        }
        
        // Add auction date at bottom if available
        if (currentAuction?.date) {
          try {
            var dateObj = new Date(currentAuction.date);
            var months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
              "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            var day = dateObj.getDate();
            var monthIndex = dateObj.getMonth();
            var year = dateObj.getFullYear();
            var formattedDate = day + ' ' + months[monthIndex] + ' ' + year;
            doc.setTextColor(150, 150, 150);
            doc.setFontSize(9);
            doc.text(formattedDate, 105 - (formattedDate.length * 1.2), 270);
          } catch (e) {
            // Skip date on error
          }
        }
        
        if (i < topplayers.length - 1) {
          doc.addPage();
        }
      });
    } else {
      // Single page layout - prioritize white background when toggled
      if (ispdfwhitebg === "1") {
        doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      } else if (aucImageBase64['2']) {
        doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      } else {
        doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      }

      if (aucImageBase64['0']) {
        doc.addImage(aucImageBase64['0'], 'JPEG', 10, 20, 40, 40);
        doc.addImage(aucImageBase64['0'], 'JPEG', 160, 20, 40, 40);
      } else {
        doc.addImage(defaultLogoImg, 'JPEG', 10, 20, 40, 40);
        doc.addImage(defaultLogoImg, 'JPEG', 160, 20, 40, 40);
      }
      
      doc.addImage(ribbin3, 'PNG', 5, -10, 200, 40);
      doc.setTextColor(34, 46, 94);

      // Add auction name
      const auctionName = currentAuction?.name || "Auction";
      if (auctionName.length < 27) {
        let alen = auctionName.length * 3.5;
        doc.text(auctionName.toUpperCase(), 105 - alen / 2, 12);
      } else {
        doc.setFontSize(15);
        var fstr = linechanger(auctionName, 27);
        let alen1 = fstr[0].length * 3.2;
        doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 10);
        if (fstr[1] && fstr[1].length <= 25) {
          doc.setFontSize(14);
          let alen2 = fstr[1].length * 2.9;
          doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 15);
        }
      }
      
      doc.addImage(ribbin, 'PNG', 55, 27, 100, 15);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text('TOP PLAYERS', 88, 33);

      // Position players based on count - exact logic from Players.jsx
      var imageX = 0, imageY = 0, imagesize = 0;
      const topNumInt = parseInt(topnum);
      
      // Set initial positions based on number of top players (from Players.jsx lines 3784-3833)
      if (topNumInt == 2 || topNumInt == 3) {
        imageX = 78; imageY = 75; imagesize = 50;
      } else if (topNumInt == 4) {
        imageX = 30; imageY = 75; imagesize = 50;
      } else if (topNumInt == 5) {
        imageX = 85; imageY = 65; imagesize = 40;
      } else if (topNumInt == 6) {
        imageX = 30; imageY = 70; imagesize = 40;
      } else if (topNumInt == 7) {
        imageX = 90; imageY = 65; imagesize = 30;
      } else if (topNumInt == 8) {
        imageX = 90; imageY = 55; imagesize = 25;
      } else if (topNumInt == 9) {
        imageX = 30; imageY = 75; imagesize = 25;
      } else if (topNumInt == 10) {
        imageX = 90; imageY = 55; imagesize = 30;
      } else if (topNumInt == 11) {
        imageX = 65; imageY = 65; imagesize = 25;
      } else if (topNumInt == 12) {
        imageX = 30; imageY = 65; imagesize = 25;
      }

      topplayers.forEach((ele, i) => {
        // Add player image (use round image if available) - except for 8 and 10 layouts which handle images specially
        if (topNumInt != 10 && topNumInt != 8) {
          if (roundPlayerImages[ele.pid]) {
            try {
              doc.addImage(roundPlayerImages[ele.pid], 'png', imageX, imageY, imagesize, imagesize);
            } catch (e) {
              console.error('Error adding round player image in top players:', e);
              // Fallback to regular image
              if (playerImages && playerImages[ele.pid]) {
                try {
                  doc.addImage(playerImages[ele.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
                } catch (e2) {
                  // Use default on error
                }
              }
            }
          } else if (playerImages && playerImages[ele.pid]) {
            try {
              doc.addImage(playerImages[ele.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
            } catch (e) {
              // Use default on error
            }
          }
        }
        
        // Layout-specific positioning and styling (from Players.jsx)
        if (topNumInt == 2) {
          doc.addImage(topborderirng, 'jpeg', imageX - 10, imageY - 10, imagesize + 20, imagesize + 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2, imageY + imagesize - 1);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.2, imageY + imagesize + 13);
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - ele.points.length * 1.4, imageY + imagesize + 20);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - teamname.length * 1.4, imageY + imagesize + 25);
            }
          }
          imageY += 100;
        } else if (topNumInt == 3) {
          doc.addImage(topborderirng, 'jpeg', imageX - 10, imageY - 10, imagesize + 20, imagesize + 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2, imageY + imagesize - 1);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 2.1, imageY + imagesize + 13);
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.9, imageY + imagesize + 20);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 25);
            }
          }
          if (i == 0) {
            imageY += 90;
            imageX -= 50;
          } else {
            imageX += 100;
          }
        } else if (topNumInt == 4) {
          doc.addImage(topborderirng, 'jpeg', imageX - 10, imageY - 10, imagesize + 20, imagesize + 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2, imageY + imagesize - 1);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 2.1, imageY + imagesize + 13);
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.9, imageY + imagesize + 20);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 25);
            }
          }
          if (i % 2 == 0 || i == 0) {
            imageX += 100;
          } else {
            imageX = 30;
            imageY += 100;
          }
        } else if (topNumInt == 5) {
          doc.addImage(topborderirng, 'jpeg', imageX - 8, imageY - 8, imagesize + 16, imagesize + 17);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2 - 0.5, imageY + imagesize);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.7, imageY + imagesize + 13);
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.9, imageY + imagesize + 20);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 25);
            }
          }
          if (i == 0) {
            imageX = 20;
            imageY += 55;
          } else if (i % 2 == 0) {
            imageX = 20;
            imageY += 85;
          } else {
            imageX += 130;
          }
        } else if (topNumInt == 6) {
          doc.addImage(topborderirng, 'jpeg', imageX - 8, imageY - 8, imagesize + 16, imagesize + 17);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(16);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2 - 0.5, imageY + imagesize);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.7, imageY + imagesize + 13);
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.9, imageY + imagesize + 20);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 25);
            }
          }
          if (i % 2 == 0 || i == 0) {
            imageX += 110;
          } else {
            imageX = 30;
            imageY += 75;
          }
        } else if (topNumInt == 7) {
          doc.addImage(topborderirng, 'jpeg', imageX - 7, imageY - 7, imagesize + 14, imagesize + 15);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(14);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2 - 0.5, imageY + imagesize + 1);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.4, imageY + imagesize + 11);
          doc.setFontSize(10);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.9, imageY + imagesize + 17);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 22);
            }
          }
          if (i == 0) {
            imageX = 30;
            imageY += 40;
          } else if (i % 2 == 0) {
            imageX = 30;
            imageY += 65;
          } else {
            imageX += 120;
          }
        } else if (topNumInt == 8) {
          // Special 8 players logic with rank swap (from Players.jsx lines 4019-4077)
          let rank = i + 1;
          let currentPlayer = ele;
          
          // Handle rank swap for positions 3 and 4
          if (i == 2) {
            currentPlayer = topplayers[i + 1];
            rank = 4;
          } else if (i == 3) {
            currentPlayer = topplayers[i - 1];
            rank = 3;
          }
          
          // Add player image for 8 players layout
          if (roundPlayerImages[currentPlayer.pid]) {
            try {
              doc.addImage(roundPlayerImages[currentPlayer.pid], 'png', imageX, imageY, imagesize, imagesize);
            } catch (e) {
              console.error('Error adding round player image in 8 players:', e);
              if (playerImages && playerImages[currentPlayer.pid]) {
                try {
                  doc.addImage(playerImages[currentPlayer.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
                } catch (e2) {}
              }
            }
          } else if (playerImages && playerImages[currentPlayer.pid]) {
            try {
              doc.addImage(playerImages[currentPlayer.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
            } catch (e) {}
          }
          
          doc.addImage(topborderirng, 'jpeg', imageX - 5, imageY - 5, imagesize + 9, imagesize + 9.5);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.text(String(rank), imageX + imagesize / 2 - 0.5, imageY + imagesize);
          doc.setTextColor(245, 175, 54);
          doc.text(currentPlayer.pname, imageX + imagesize / 2 - currentPlayer.pname.length * 1.4, imageY + imagesize + 9);
          doc.setFontSize(10);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(currentPlayer.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (currentPlayer.points.length + 5) * 0.9, imageY + imagesize + 15);
          if (teams && teams.length > 0 && currentPlayer.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == currentPlayer.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8), imageY + imagesize + 20);
            }
          }
          
          // Special positioning for 8 players
          if (i == 0) {
            imageX = 30;
            imageY += 35;
          } else if (i == 1) {
            imageX += 60;
            imageY += 18;
          } else if (i == 2) {
            imageX += 60;
            imageY -= 18;
          } else if (i % 2 !== 0) {
            imageX = 30;
            imageY += 65;
          } else {
            imageX += 120;
          }
        } else if (topNumInt == 9) {
          doc.addImage(topborderirng, 'jpeg', imageX - 5, imageY - 5, imagesize + 9, imagesize + 9.5);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2 - 1.5, imageY + imagesize - 0.5);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.4, imageY + imagesize + 9);
          doc.setFontSize(8);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.7, imageY + imagesize + 15);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8) * 0.8, imageY + imagesize + 19);
            }
          }
          
          if ((i + 1) % 3 == 0) {
            imageX = 30;
            imageY += 65;
          } else {
            imageX += 65;
          }
        } else if (topNumInt == 10) {
          // Special 10 players logic with rank swap (from Players.jsx lines 4106-4150)
          let rank = i + 1;
          let currentPlayer = ele;
          
          // Handle rank swap for positions 3 and 4
          if (i == 2) {
            currentPlayer = topplayers[i + 1];
            rank = 4;
          } else if (i == 3) {
            currentPlayer = topplayers[i - 1];
            rank = 3;
          }
          
          // Add player image for 10 players layout
          if (roundPlayerImages[currentPlayer.pid]) {
            try {
              doc.addImage(roundPlayerImages[currentPlayer.pid], 'png', imageX, imageY, imagesize, imagesize);
            } catch (e) {
              console.error('Error adding round player image in 10 players:', e);
              if (playerImages && playerImages[currentPlayer.pid]) {
                try {
                  doc.addImage(playerImages[currentPlayer.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
                } catch (e2) {}
              }
            }
          } else if (playerImages && playerImages[currentPlayer.pid]) {
            try {
              doc.addImage(playerImages[currentPlayer.pid], 'jpeg', imageX, imageY, imagesize, imagesize);
            } catch (e) {}
          }
          
          doc.addImage(topborderirng, 'jpeg', imageX - 5, imageY - 5, imagesize + 9, imagesize + 10.5);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.text(String(rank), imageX + imagesize / 2 - 1.5, imageY + imagesize - 1.5);
          doc.setTextColor(245, 175, 54);
          doc.text(currentPlayer.pname, imageX + imagesize / 2 - currentPlayer.pname.length * 1.4, imageY + imagesize + 8);
          doc.setFontSize(8);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(currentPlayer.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (currentPlayer.points.length + 5) * 0.7, imageY + imagesize + 13);
          if (teams && teams.length > 0 && currentPlayer.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == currentPlayer.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8) * 0.8, imageY + imagesize + 17);
            }
          }
          
          // Special positioning for 10 players
          if (i == 0) {
            imageX = 30;
            imageY += 35;
          } else if (i % 3 == 0) {
            imageX = 30;
            imageY += 65;
          } else {
            imageX += 60;
          }
        } else {
          // For other counts (11, 12) - use existing simplified logic
          doc.addImage(topborderirng, 'jpeg', imageX - 10, imageY - 10, imagesize + 20, imagesize + 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.text(String((i + 1)), imageX + imagesize / 2, imageY + imagesize - 1);
          doc.setTextColor(245, 175, 54);
          doc.text(ele.pname, imageX + imagesize / 2 - ele.pname.length * 1.2, imageY + imagesize + 13);
          doc.setFontSize(8);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'normal');
          doc.text(String(parseFloat(ele.points)) + (currentAuction?.unit || '') + ' POINTS', imageX + imagesize / 2 - (ele.points.length + 5) * 0.7, imageY + imagesize + 18);
          if (teams && teams.length > 0 && ele.tmid > 0) {
            var teamname = teams.find((tm) => tm.tid == ele.tmid)?.name || '';
            if (teamname) {
              doc.text('TEAM : ' + teamname.toUpperCase(), imageX + imagesize / 2 - (teamname.length + 8) * 0.8, imageY + imagesize + 22);
            }
          }
          
          // Positioning for 11, 12 players
          if (i % 4 == 0 && i !== 0) {
            imageX = 30; imageY += 55;
          } else if (i !== 0) {
            imageX += 45;
          }
        }
      });
    }

    const auctionId = currentAuction?.aid || "auction";
    const auctionNameForFilename = (currentAuction?.name || "Auction").toUpperCase().replace(/[^a-z0-9]/gi, '_');
    doc.save(`${auctionId}-${auctionNameForFilename}_TOP-PLIST.pdf`);
  };

  // Player-wise PDF generation
  const generatePlayerWisePDF = () => {
    var doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [200, 220]
    });
    var soldcount = 0;
    
    data.data.list.forEach((ele, i) => {
      // Add background
      doc.addImage(plbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      
      // Add logos
      if (aucImageBase64['0']) {
        doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 40, 40);
        doc.addImage(aucImageBase64['0'], 'JPEG', 150, 10, 40, 40);
      } else {
        doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 40, 40);
        doc.addImage(defaultLogoImg, 'JPEG', 150, 10, 40, 40);
      }
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(255, 255, 255);

      // Player info box
      doc.setFillColor(171, 212, 208);
      doc.roundedRect(60, 65, 75, 75, 2, 2, 'F');
      doc.rect(15, 159, 125, 15, 'F');

      doc.setTextColor(31, 29, 122);
      let plen = ele.pname.length * 4;
      doc.text(ele.pname.toUpperCase(), (62.5 - plen / 2) + 15, 169);
      doc.setTextColor(255, 255, 255);

      doc.setFillColor(18, 62, 138);
      doc.rect(15, 174, 125, 15, 'F');
      doc.rect(140, 149, 43, 43, 'F');
      
      // Add team logo if player is sold
      if (parseInt(ele.tmid) > 0) {
        if (aucImageBase64[ele.tmid]) {
          doc.addImage(aucImageBase64[ele.tmid], 'JPEG', 141, 150, 41, 41);
        } else {
          doc.addImage(teamdefaultLogo, 'JPEG', 141, 150, 41, 41);
        }
      }
      
      // Add player image
      if (playerImages && playerImages[ele.pid]) {
        try {
          doc.addImage(playerImages[ele.pid], 'JPEG', 61, 66, 73, 73);
        } catch (e) {
          doc.addImage(playerdefaultLogo, 'JPEG', 61, 66, 73, 73);
        }
      } else {
        doc.addImage(playerdefaultLogo, 'JPEG', 61, 66, 73, 73);
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('WILL PLAY FOR ', 65, 182);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');

      // Add auction name at bottom
      doc.setFillColor(18, 62, 138);
      doc.rect(1, 204, 198, 15, 'F');
      doc.setFontSize(18);
      
      const auctionName = currentAuction?.name || "Auction";
      let alen = auctionName.length * 4;
      doc.setFont('helvetica', 'bold');
      doc.text(auctionName.toUpperCase(), 100 - alen / 2, 213);

      if (i < data.data.list.length - 1) {
        doc.addPage();
      }
      
      if (ele.tmid > 0) {
        soldcount += 1;
      }
    });
    
    if (soldcount > 0) {
      const auctionId = currentAuction?.aid || "auction";
      const auctionNameForFilename = (currentAuction?.name || "Auction").toUpperCase().replace(/[^a-z0-9]/gi, '_');
      doc.save(`${auctionId}-${auctionNameForFilename}_PLAYER-WISE.pdf`);
    } else {
      toast.warning("No player sold till now!");
    }
  };

  const handleGeneratePdf = () => {
    if (!data || !data.data || !data.data.list || data.data.list.length === 0) {
      toast.error("No player data available");
      return;
    }

    try {
      // Handle special PDF types first
      if (pdfType === 'top') {
        generateTopPlayersPDF();
        return;
      }
      
      if (pdfType === 'player') {
        generatePlayerWisePDF();
        return;
      }
      
      if (pdfType === 'owners') {
        generateTagwisePDF();
        return;
      }
      // Get the full player list
      let pdfdata = [...data.data.list];
      
      // Apply orderby1 sorting logic (from Players.jsx lines 964-1054)
      if (orderBy === '2') {
        // Sort by player name
        pdfdata = pdfdata.sort((a, b) =>
          a.pname.toLowerCase() < b.pname.toLowerCase() ? -1 : 1
        );
      } else if (orderBy === '1') {
        // Sort by player code (number)
        pdfdata = pdfdata.sort((a, b) =>
          Number(a.pcode) < Number(b.pcode) ? -1 : 1
        );
      } else if (orderBy === '5' || pdfType === 'team') {
        // Team-wise sorting with various sub-options
        let teamwisefull = [];
        
        if (teams && teams.length > 0) {
          teams.forEach((tm) => {
            let teamwise = pdfdata.filter((dt) => dt.tmid == tm.tid);
            
            if (orderBy === '5') {
              // Sort by sold sequence
              if (teamwise.length > 0) {
                teamwise.sort((a, b) => a.soldseq - b.soldseq);
              }
            } else if (orderBy === '7') {
              // Sort by points/value
              if (teamwise.length > 0) {
                teamwise.sort((a, b) => b.points - a.points);
              }
            } else if (orderBy === '8') {
              // Sort by player name within team
              if (teamwise.length > 0) {
                teamwise.sort((a, b) =>
                  a.pname.toLowerCase() < b.pname.toLowerCase() ? -1 : 1
                );
              }
            } else if (orderBy === '6' && categories && categories.length > 0) {
              // Category-wise within team - handle properly to avoid duplication
              let teamcatwise = [];
              if (teamwise.length > 0) {
                categories.forEach((ct) => {
                  let catteam = teamwise.filter((tw) => ct.cid == tw.catid);
                  catteam.forEach((ele) => {
                    teamcatwise.push(ele);
                  });
                });
                // Add players with no category
                teamwise.filter((tw) => tw.catid == "0").forEach((tw) => {
                  teamcatwise.push(tw);
                });
              }
              teamcatwise.forEach((ele) => {
                teamwisefull.push(ele);
              });
            } else {
              // Default team-wise
              teamwise.forEach((ele) => {
                teamwisefull.push(ele);
              });
            }
          });
        }
        
        pdfdata = teamwisefull;
      }
      
      // Apply orderby2 sorting logic (category-wise) (from Players.jsx lines 1056-1109)
      if (pdfType === 'category' || orderBy === '3' || orderBy === '4') {
        if (categories && categories.length > 0) {
          let catwisefull = [];
          let generatecat = categories; // Use all categories for now
          
          generatecat.forEach((ct) => {
            let catwise = data.data.list.filter((dt) => dt.catid == ct.cid);
            if (catwise.length > 0) {
              if (orderBy === '2' || orderBy === '4') {
                // Sort by player name within category
                catwise.sort((a, b) =>
                  a.pname.toLowerCase() < b.pname.toLowerCase() ? -1 : 1
                );
              } else if (orderBy === '1') {
                // Sort by player code within category
                catwise.sort((a, b) => parseInt(a.pcode) - parseInt(b.pcode));
              } else {
                // Default sort by player code
                catwise.sort((a, b) => parseInt(a.pcode) - parseInt(b.pcode));
              }
              
              catwise.forEach((ele) => {
                catwisefull.push(ele);
              });
            }
          });
          
          pdfdata = catwisefull;
          
          // Add no-category players if enabled
          if (isNoCatShow) {
            pdfdata.push(...data.data.list.filter((pl) => pl.catid == "0"));
          }
        }
      }
      
      // Apply status filtering (from Players.jsx lines 1144-1169)
      if (playerFilter === "" || playerFilter === "1") {
        // Keep all players
      } else {
        if (playerFilter === '2') {
          // Available players
          pdfdata = pdfdata.filter((pl) => (pl.tmid == 0 || pl.tmid == "") && pl.isskiped != '1' && pl.isnonplay != '1');
        } else if (playerFilter === '3') {
          // Sold players
          pdfdata = pdfdata.filter((pl) => pl.tmid > 0);
        } else if (playerFilter === '4') {
          // Skipped players
          pdfdata = pdfdata.filter((pl) => pl.isskiped == '1');
        } else if (playerFilter === '5' || playerFilter === '6') {
          // Unsold + available
          pdfdata = pdfdata.filter((pl) => pl.tmid == 0 || pl.tmid == "");
        }
      }

      if (pdfdata.length === 0) {
        toast.warning('No players match the selected criteria');
        return;
      }

      // Check if we should use circular layout (Type 2)
      if (pdfLayout === '2') {
        const doc = generateCircularLayoutPDF(pdfdata);
        
        // Save the PDF with appropriate filename
        const auctionId = currentAuction?.aid || "auction";
        const auctionNameForFilename = (currentAuction?.name || "Auction").toUpperCase().replace(/[^a-z0-9]/gi, '_');
        
        let filename = `${auctionId}-${auctionNameForFilename}_PLIST`;
        
        if (orderBy === '3' || orderBy === '4') {
          filename += '_CATEGORY';
        } else if (pdfType === 'team') {
          filename += '_TEAM';
        }
        
        filename += '_CIRCULAR.pdf';
        doc.save(filename);
        
        toast.success("PDF downloaded successfully!");
        onClose();
        return;
      }

      // Initialize PDF document for Type 1 layout
      const doc = new jsPDF({
        orientation: orientation,
        unit: 'mm'
      });
      
      // Set layout parameters based on column count and per page players
      let column, perpage, rightbox, bottombox, rectwide, reactheight;
      let imgwidth, imgheight, nameagefont, namex, namey, plnox, plnoy;
      let spcffont, spcfx, spcfy, extra = 2;

      // Determine column count (from Players.jsx lines 1117-1125)
      if (columnCount === '2') {
        column = 2;
      } else if (columnCount === '3') {
        column = 3;
      } else {
        column = 2; // Default to 2
      }

      // Set layout parameters based on column and perpage settings (from Players.jsx lines 1190-1385)
      
      // Determine perpage based on column and setting (from Players.jsx lines 1127-1140)
      if (perPagePlayers === "" && columnCount === '3') {
        perpage = 27;
      } else if (perPagePlayers === "" && columnCount === '2') {
        perpage = 14;
      } else if (perPagePlayers !== '') {
        perpage = parseInt(perPagePlayers);
      } else {
        perpage = 14;
      }
      
      // Layout parameters based on exact column and perpage combinations
      if (column == 2 && perpage == 12) {
        rightbox = 98;
        bottombox = 40;
        rectwide = 94;
        reactheight = 32;
        imgwidth = 30;
        imgheight = 30;
        nameagefont = 12;
        namex = 33;
        namey = 5;
        plnox = 33;
        plnoy = 11;
        spcffont = 10;
        spcfx = 33;
        spcfy = 20;
        extra = 2;
      } else if (column == 2 && perpage == 14) {
        rightbox = 98;
        bottombox = 33;
        rectwide = 94;
        reactheight = 29;
        imgwidth = 27;
        imgheight = 27;
        nameagefont = 12;
        namex = 30;
        namey = 5;
        plnox = 30;
        plnoy = 11;
        spcffont = 10;
        spcfx = 30;
        spcfy = 20;
        extra = 2;
      } else if (column == 2 && perpage == 16) {
        rightbox = 98;
        bottombox = 29;
        rectwide = 94;
        reactheight = 26;
        imgwidth = 24;
        imgheight = 24;
        nameagefont = 10;
        namex = 28;
        namey = 5;
        plnox = 28;
        plnoy = 11;
        spcffont = 9;
        spcfx = 28;
        spcfy = 20;
        extra = 2;
      } else if (column == 3 && perpage == 24) {
        rightbox = 65;
        bottombox = 29;
        rectwide = 62;
        reactheight = 22;
        imgwidth = 20;
        imgheight = 20;
        nameagefont = 8;
        namex = 22;
        namey = 4;
        plnox = 22;
        plnoy = 9;
        spcffont = 7;
        spcfx = 22;
        spcfy = 16;
        extra = 6;
      } else if (column == 3 && perpage == 27) {
        rightbox = 65;
        bottombox = 26;
        rectwide = 62;
        reactheight = 22;
        imgwidth = 20;
        imgheight = 20;
        nameagefont = 8;
        namex = 22;
        namey = 4;
        plnox = 22;
        plnoy = 9;
        spcffont = 7;
        spcfx = 22;
        spcfy = 16;
        extra = 6;
      } else if (column == 3 && perpage == 30) {
        rightbox = 65;
        bottombox = 23;
        rectwide = 62;
        reactheight = 22;
        imgwidth = 20;
        imgheight = 20;
        nameagefont = 8;
        namex = 22;
        namey = 4;
        plnox = 22;
        plnoy = 9;
        spcffont = 7;
        spcfx = 22;
        spcfy = 16;
        extra = 6;
      } else if (column == 3 && perpage == 33) {
        rightbox = 65;
        bottombox = 21;
        rectwide = 62;
        reactheight = 20;
        imgwidth = 18;
        imgheight = 18;
        nameagefont = 7;
        namex = 21;
        namey = 4;
        plnox = 21;
        plnoy = 9;
        spcffont = 6;
        spcfx = 21;
        spcfy = 14;
        extra = 6;
      } else {
        // Default to 3x27
        rightbox = 65;
        bottombox = 26;
        rectwide = 62;
        reactheight = 22;
        imgwidth = 20;
        imgheight = 20;
        nameagefont = 8;
        namex = 22;
        namey = 4;
        plnox = 22;
        plnoy = 9;
        spcffont = 7;
        spcfx = 22;
        spcfy = 16;
        extra = 6;
      }
      
      // Setup initial variables
      var counter = 0;
      var count2 = 0;
      let x = 10;
      let y = 60;

      // Add background image - prioritize white background when toggled (fixed from Players.jsx)
      if (ispdfwhitebg === "1") {
        doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      } else if (aucImageBase64['2']) {
        doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      } else {
        doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
      }

      // Add logos using base64 images (similar to Players.jsx lines 1465-1483)
      if (aucImageBase64['0']) {
        doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 40, 40);
        if (isSpnShow && aucImageBase64['1']) {
          doc.addImage(aucImageBase64['1'], 'JPEG', 160, 10, 40, 40);
        } else {
          doc.addImage(aucImageBase64['0'], 'JPEG', 160, 10, 40, 40);
        }
      } else {
        doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 40, 40);
        doc.addImage(defaultLogoImg, 'JPEG', 160, 10, 40, 40);
      }
      
      // For team-wise PDFs, replace left logo with team logo (similar to Players.jsx lines 1484-1489)
      if (pdfType === 'team' && pdfdata.length > 0 && teams) {
        const firstPlayerTeam = teams.find(team => team.tid == pdfdata[0].tmid);
        console.log('Team-wise PDF detected:');
        console.log('First player team ID:', pdfdata[0].tmid);
        console.log('Found team:', firstPlayerTeam);
        console.log('Available base64 images:', Object.keys(aucImageBase64));
        console.log('Team logo base64 available:', !!aucImageBase64[firstPlayerTeam?.tid]);
        
        if (firstPlayerTeam && aucImageBase64[firstPlayerTeam.tid]) {
          console.log(`Adding team logo for ${firstPlayerTeam.name} at position (10,10)`);
          doc.addImage(aucImageBase64[firstPlayerTeam.tid], 'JPEG', 10, 10, 40, 40);
        } else {
          console.log('Team logo not available, keeping auction logo');
        }
      }

      // Set font and text color (from Players.jsx lines 1496-1505)
      try {
        doc.setFont("NotoSansGujarati-Regular", "normal");
      } catch (e) {
        // Fallback to default font if NotoSansGujarati is not available
        doc.setFont("helvetica", "normal");
      }
      
      // Use dynamic font color based on background
      if (fontcolor == 0) {
        doc.setTextColor(255, 255, 255);
      } else {
        doc.setTextColor(20, 14, 92);
      }
      doc.setFontSize(18);
      
      // Add auction name
      const auctionName = currentAuction?.name || "Auction";
      if (auctionName.length < 27) {
        let alen = auctionName.length * 4;
        doc.text(auctionName.toUpperCase(), 105 - alen / 2, 18);
      } else {
        doc.setFontSize(15);
        var fstr = linechanger(auctionName, 27);
        let alen1 = fstr[0].length * 3.2;
        doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 18);
        if (fstr[1].length <= 25) {
          doc.setFontSize(14);
          let alen2 = fstr[1].length * 2.9;
          doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 24);
        } else {
          var fsstr = linechanger(fstr[1], 25);
          doc.setFontSize(14);
          let alen2 = fsstr[0].length * 2.9;
          let alen3 = fsstr[1].length * 2.9;
          doc.text(fsstr[0].toUpperCase(), 105 - alen2 / 2, 24);
          doc.text(fsstr[1].toUpperCase(), 105 - alen3 / 2, 30);
        }
      }
      
      doc.setFontSize(10);
      
      // Add subtitle based on PDF type and player filter (from Players.jsx lines 1542-1572)
      if (pdfType === 'team' || pdfType === 'category') {
        // For team/category PDFs, subtitle will be added per section
      } else {
        if (fontcolor == 0) {
          doc.setTextColor(196, 147, 14);
        } else {
          doc.setTextColor(22, 14, 117);
        }
        
        let subtitle = '';
        if (playerFilter === '4' || playerFilter === '5') {
          subtitle = 'UNSOLD PLAYER LIST';
        } else if (playerFilter === '2' || playerFilter === '6') {
          subtitle = 'AVAILABLE PLAYER LIST';
        } else {
          subtitle = 'AUCTION PLAYER LIST';
        }
        doc.text(subtitle, 85, 37);
        
        if (fontcolor == 0) {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(20, 14, 92);
        }
      }
      doc.setFontSize(16);

      // Initialize tracking variables for category/team changes (from Players.jsx lines 1577-1579)
      var dumcatid = pdfdata.length > 0 ? pdfdata[0].catid : "";
      var dumtid = pdfdata.length > 0 ? pdfdata[0].tmid : "";
      
      // Function to add page header (extracted from Players.jsx logic)
      const addPageHeader = (doc, categoryName = null, teamName = null) => {
        // Add background using base64 - prioritize white background when toggled
        if (ispdfwhitebg === "1") {
          doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
        } else if (aucImageBase64['2']) {
          doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
        } else {
          doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
        }
        
        // Add logos using base64 images
        if (aucImageBase64['0']) {
          doc.addImage(aucImageBase64['0'], 'JPEG', 10, 10, 40, 40);
          if (isSpnShow && aucImageBase64['1']) {
            doc.addImage(aucImageBase64['1'], 'JPEG', 160, 10, 40, 40);
          } else {
            doc.addImage(aucImageBase64['0'], 'JPEG', 160, 10, 40, 40);
          }
        } else {
          doc.addImage(defaultLogoImg, 'JPEG', 10, 10, 40, 40);
          doc.addImage(defaultLogoImg, 'JPEG', 160, 10, 40, 40);
        }
        
        // For team-wise PDFs, replace left logo with team logo
        if (teamName && teams) {
          const team = teams.find(t => t.name === teamName);
          console.log(`addPageHeader: Looking for team with name: ${teamName}`);
          console.log(`addPageHeader: Found team:`, team);
          console.log(`addPageHeader: Team logo base64 available:`, !!aucImageBase64[team?.tid]);
          
          if (team && aucImageBase64[team.tid]) {
            console.log(`addPageHeader: Adding team logo for ${teamName} at position (10,10)`);
            doc.addImage(aucImageBase64[team.tid], 'JPEG', 10, 10, 40, 40);
          } else {
            console.log(`addPageHeader: Team logo not available for ${teamName}`);
          }
        }
        
        // Add auction name
        try {
          doc.setFont("NotoSansGujarati-Regular", "normal");
        } catch (e) {
          doc.setFont("helvetica", "normal");
        }
        
        if (fontcolor == 0) {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(20, 14, 92);
        }
        doc.setFontSize(18);
        
        const auctionName = currentAuction?.name || "Auction";
        if (auctionName.length < 27) {
          let alen = auctionName.length * 4;
          doc.text(auctionName.toUpperCase(), 105 - alen / 2, 18);
        } else {
          doc.setFontSize(15);
          var fstr = linechanger(auctionName, 27);
          let alen1 = fstr[0].length * 3.2;
          doc.text(fstr[0].toUpperCase(), 105 - alen1 / 2, 18);
          if (fstr[1].length <= 25) {
            doc.setFontSize(14);
            let alen2 = fstr[1].length * 2.9;
            doc.text(fstr[1].toUpperCase(), 105 - alen2 / 2, 24);
          } else {
            var fsstr = linechanger(fstr[1], 25);
            doc.setFontSize(14);
            let alen2 = fsstr[0].length * 2.9;
            let alen3 = fsstr[1].length * 2.9;
            doc.text(fsstr[0].toUpperCase(), 105 - alen2 / 2, 24);
            doc.text(fsstr[1].toUpperCase(), 105 - alen3 / 2, 30);
          }
        }
        
        doc.setFontSize(16);
        
        // Add category or team name
        if (categoryName) {
          const categoryText = categoryName.toUpperCase();
          const categoryTextWidth = doc.getTextWidth(categoryText);
          doc.text(categoryText, (doc.internal.pageSize.getWidth() - categoryTextWidth) / 2, 35);
          doc.setFontSize(8);
          const playerListText = 'PLAYER LIST';
          const playerListTextWidth = doc.getTextWidth(playerListText);
          doc.text(playerListText, (doc.internal.pageSize.getWidth() - playerListTextWidth) / 2, 40);
          doc.setFontSize(16);
        } else if (teamName) {
          const teamText = teamName.toUpperCase();
          const teamTextWidth = doc.getTextWidth(teamText);
          doc.text(teamText, (doc.internal.pageSize.getWidth() - teamTextWidth) / 2, 35);
        }
      };

      // Main player rendering loop
      pdfdata.forEach((player, index) => {
        // Handle category-wise page breaks (from Players.jsx lines 1625-1841)
        if (pdfType === 'category' || orderBy === '3' || orderBy === '4') {
          if (dumcatid !== player.catid && isNoCatShow && player.catid == "0") {
            dumcatid = player.catid;
            counter = 0;
            count2 = 0;
            doc.addPage();
            addPageHeader(doc, "No category");
            x = 10;
            y = 60;
          } else if (dumcatid !== player.catid) {
            dumcatid = player.catid;
            counter = 0;
            count2 = 0;
            doc.addPage();
            const catname = categories?.find(ct => ct.cid === player.catid)?.cname || "Category";
            addPageHeader(doc, catname);
            x = 10;
            y = 60;
          }
        }
        
        // Handle team-wise page breaks (from Players.jsx lines 1842-1948)
        if (pdfType === 'team' || orderBy === '5' || orderBy === '7' || orderBy === '8') {
          if (dumtid !== player.tmid) {
            dumtid = player.tmid;
            counter = 0;
            count2 = 0;
            doc.addPage();
            const teamname = teams?.find(tm => tm.tid == player.tmid)?.name || "Team";
            addPageHeader(doc, null, teamname);
            x = 10;
            y = 60;
          }
        }
        
        // Add initial category/team header on first page
        if (count2 == 0) {
          if (pdfType === 'category' || orderBy === '3' || orderBy === '4') {
            let catname;
            if (player.catid == "0") {
              catname = "No category";
            } else {
              catname = categories?.find(ct => ct.cid == player.catid)?.cname || "Category";
            }
            // Add the category header text to the first page
            doc.setFontSize(16);
            if (fontcolor == 0) {
              doc.setTextColor(255, 255, 255);
            } else {
              doc.setTextColor(20, 14, 92);
            }
            const categoryText = catname.toUpperCase();
            const categoryTextWidth = doc.getTextWidth(categoryText);
            doc.text(categoryText, (doc.internal.pageSize.getWidth() - categoryTextWidth) / 2, 35);
            doc.setFontSize(8);
            const playerListText = 'PLAYER LIST';
            const playerListTextWidth = doc.getTextWidth(playerListText);
            doc.text(playerListText, (doc.internal.pageSize.getWidth() - playerListTextWidth) / 2, 40);
            doc.setFontSize(16);
          }
          if (pdfType === 'team' || orderBy === '5' || orderBy === '7' || orderBy === '8') {
            const teamname = teams?.find(tm => tm.tid == player.tmid)?.name || "Team";
            // Add the team header text to the first page
            doc.setFontSize(16);
            if (fontcolor == 0) {
              doc.setTextColor(255, 255, 255);
            } else {
              doc.setTextColor(20, 14, 92);
            }
            const teamText = teamname.toUpperCase();
            const teamTextWidth = doc.getTextWidth(teamText);
            doc.text(teamText, (doc.internal.pageSize.getWidth() - teamTextWidth) / 2, 35);
          }
        }
        // Handle column positioning
        if (column === 2) {
          if (counter % 2 && counter !== 0) {
            x = x + rightbox;
          } else if (counter !== 0) {
            y = y + bottombox;
            x = 10;
          }
        } else if (column === 3) {
          if (counter % 3 && counter !== 0) {
            x = x + rightbox;
          } else if (counter !== 0) {
            y = y + bottombox;
            x = 10;
          }
        }

        // Draw player card rectangle
        doc.setDrawColor(232, 192, 84);
        doc.rect(x - 1, y - 1, rectwide, reactheight);

        // Draw player image background
        doc.setDrawColor(252, 151, 28);
        doc.setFillColor(252, 151, 28);
        doc.rect(x - 0.5, y - 0.5, imgwidth + 1, imgheight + 1, 'F');

        // Add player image
        const playerImage = playerImages && playerImages[player.pid] ? playerImages[player.pid] : defaultPlayerImg;
        try {
          doc.addImage(playerImage, 'PNG', x, y, imgwidth, imgheight);
        } catch (e) {
          doc.addImage(defaultPlayerImg, 'PNG', x, y, imgwidth, imgheight);
        }

        // Draw player number box
        doc.setFillColor(252, 151, 28);
        doc.rect(x - 2, y - 2, 4.5, 4.5, 'F');

        // Add player number (pcode) to orange box
        if (isPlNoShow && player.pcode) {
          if (player.pcode.length === 1) {
            doc.setFontSize(10);
            doc.text(player.pcode, x - 0.9, y + 1.3);
          } else if (player.pcode.length === 2) {
            doc.setFontSize(8.5);
            doc.text(player.pcode, x - 1.6, y + 1.3);
          } else if (player.pcode.length === 3) {
            doc.setFontSize(7);
            doc.text(player.pcode, x - 1.8, y + 1.3);
          }
        }

        // Add player name (from Players.jsx lines 2010-2037)
        doc.setFontSize(nameagefont);
        try {
          doc.setFont("NotoSansGujarati-Regular", "bold");
        } catch (e) {
          doc.setFont("helvetica", "bold");
        }
        
        if (player.pname.length > 20) {
          doc.setFontSize(6);
        } else {
          doc.setFontSize(8);
        }
        
        try {
          doc.setFont("NotoSansGujarati-Regular", "normal");
        } catch (e) {
          doc.setFont("helvetica", "normal");
        }
        
        doc.text(player.pname.toUpperCase(), x + namex, y + namey - 1);
        doc.setFontSize(10);

        // Variable to track Y position for additional text
        var ym = 3;
        if (player.age === '0') {
          ym = 0;
        }

        // Player age
        if (player.age !== "0") {
          doc.setFontSize(6);
          doc.text(`AGE: ${player.age} YR`, x + plnox, y + plnoy - 1.1);
        }

        // Mobile number (if enabled)
        if (isMobNoShow && player.mob) {
          doc.setFontSize(6);
          doc.text(player.mob, x + plnox + 23, y + plnoy - 2.1);
        }

        // Player value and team info (for sold players)
        if (isValShow && player.tmid !== "0" && player.tmid !== "") {
          doc.setFontSize(10);
          if (fontcolor == 0) {
            doc.setTextColor(222, 235, 52);
          } else {
            doc.setTextColor(12, 79, 17);
          }
          if (currentAuction?.isdecm === '1') {
            doc.text(parseFloat(player.points).toFixed(2), x + plnox, y + plnoy + ym - 1);
            var temppx = player.points.length * 1.5;
          } else {
            doc.text(String(parseInt(player.points)), x + plnox, y + plnoy + ym - 1);
            var temppx = String(parseInt(player.points)).length * 2;
          }
          doc.setFontSize(6);
          if (currentAuction?.unit) {
            doc.text(currentAuction.unit + " POINTS", x + plnox + temppx, y + plnoy + ym - 1.5);
          } else {
            doc.text("POINTS", x + plnox + temppx + 1, y + plnoy + ym - 1.5);
          }
          if (fontcolor == 0) {
            doc.setTextColor(255, 255, 255);
          } else {
            doc.setTextColor(20, 14, 92);
          }
          if (columnCount === '1') {
            ym += 4.5;
          } else {
            ym += 3;
          }
        }

        if (fontcolor == 0) {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(20, 14, 92);
        }
        doc.setFontSize(6);

        // Show team info for sold players
        if (isValShow && String(player.tmid) !== '0') {
          doc.text('SOLD TO :', x + plnox, y + plnoy + ym - 1);
          const pteam = teams?.find(team => team.tid === player.tmid)?.name;
          if (pteam) {
            doc.text(pteam.toUpperCase(), x + plnox + 11, y + plnoy + ym - 1);
          }
          ym += 2.2;
        }

        // Owner tag
        if (isOwnerShow && player.ownertag !== '0') {
          doc.setFontSize(6);
          if (fontcolor == 0) {
            doc.setTextColor(222, 235, 52);
          } else {
            doc.setTextColor(12, 79, 17);
          }
          let tag = '';
          if (player.ownertag === '1') {
            tag = 'Owner';
          } else if (player.ownertag === '2') {
            tag = 'Co-owner';
          } else if (player.ownertag === '3') {
            tag = 'Captain';
          } else if (player.ownertag === '4') {
            tag = 'Icon';
          }
          doc.text(tag, x + plnox, y + plnoy + ym - 1);
          ym += 2.2;
        }

        if (fontcolor == 0) {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(20, 14, 92);
        }
        
        // Category
        if (player.catid !== "0" && player.catid !== null && isCatShow) {
          doc.setFontSize(6);
          const catname = categories?.find(ct => ct.cid === player.catid)?.cname;
          if (catname) {
            doc.text(catname.toUpperCase(), x + plnox, y + plnoy + ym - 1);
          }
          ym += 2.2;
        }

        // Base price
        if (isBasePriceShow) {
          const unit = currentAuction?.unit || '';
          if (player.base && player.base !== '' && player.base !== '0.00') {
            doc.text('Base price : ' + parseFloat(player.base).toFixed(currentAuction?.isdecm || 0) + ' ' + unit, x + plnox, y + plnoy + ym - 1);
            ym += 2.2;
          }
        }

        if (fontcolor == 0) {
          doc.setTextColor(232, 209, 188);
        } else {
          doc.setTextColor(22, 14, 117);
        }
        
        // T-shirt size
        if (isTshirtShow && player.tshirt !== "" && player.tshirt !== null) {
          doc.setFontSize(5);
          doc.text('T-' + player.tshirt, x + plnox, y + plnoy + ym - 1);
          ym += 2.2;
        }

        doc.setFontSize(5.6);

        // Specifications
        if (player.spcf1 && player.spcf1 !== '' && isSpec1) {
          if (player.spcf1.length <= 28) {
            doc.text(player.spcf1.toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          } else {
            doc.setFontSize(5);
            const fstr = linechanger(player.spcf1, 29);
            doc.text(fstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            ym += 1.5;
            doc.text(fstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          }
          ym += 2.2;
        }

        if (player.spcf2 && isSpec2) {
          if (player.spcf2.length <= 28) {
            doc.text(player.spcf2.toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          } else {
            doc.setFontSize(5);
            const fsstr = linechanger(player.spcf2, 29);
            doc.text(fsstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            ym += 1.5;
            doc.text(fsstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          }
          ym += 2.2;
        }

        if (player.spcf3 && isSpec3) {
          if (player.spcf3.length <= 28) {
            doc.text(player.spcf3.toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          } else {
            doc.setFontSize(4.5);
            const fsstr = linechanger(player.spcf3, 29);
            doc.text(fsstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            ym += 1.5;
            if (fsstr[1].length < 29) {
              doc.text(fsstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            } else {
              const fssstr = linechanger(fsstr[1], 29);
              doc.text(fssstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
              ym += 1.5;
              doc.text(fssstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            }
          }
          ym += 2.2;
        }

        // Detail
        if (player.detail && isDetailShow) {
          if (player.detail.length <= 28) {
            doc.text(player.detail.toUpperCase(), x + spcfx, y + plnoy + ym - 1);
          } else {
            doc.setFontSize(4.5);
            const fsstr = linechanger(player.detail, 29);
            doc.text(fsstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            ym += 1.5;
            if (fsstr[1].length < 29) {
              doc.text(fsstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            } else {
              const fssstr = linechanger(fsstr[1], 29);
              doc.text(fssstr[0].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
              ym += 1.5;
              doc.text(fssstr[1].toUpperCase(), x + spcfx, y + plnoy + ym - 1);
            }
          }
          ym += 2.2;
        }

        counter++;
        count2++;

        if (fontcolor == 0) {
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setTextColor(20, 14, 92);
        }
        
        // Handle page breaks (from Players.jsx lines 2349-2391)
        if (count2 > perpage) {
          if (counter % (perpage + extra) == 0) {
            counter = 0;
            doc.addPage();
            
            // Add background to new page using base64 - prioritize white background when toggled
            if (ispdfwhitebg === "1") {
              doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            } else if (aucImageBase64['2']) {
              doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            } else {
              doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            }
            
            x = 10;
            y = 10;
          }
        } else {
          if (counter % perpage == 0) {
            counter = 0;
            doc.addPage();
            
            // Add background to new page using base64 - prioritize white background when toggled
            if (ispdfwhitebg === "1") {
              doc.addImage(whitebg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            } else if (aucImageBase64['2']) {
              doc.addImage(aucImageBase64['2'], 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            } else {
              doc.addImage(pdfbg, 'JPEG', 1, 1, doc.internal.pageSize.width - 2, doc.internal.pageSize.height - 2);
            }
            
            x = 10;
            y = 10;
          }
        }
      });

      // Save the PDF with appropriate filename
      const auctionId = currentAuction?.aid || "auction";
      const auctionNameForFilename = (currentAuction?.name || "Auction").toUpperCase().replace(/[^a-z0-9]/gi, '_');
      
      let filename = `${auctionId}-${auctionNameForFilename}_PLIST`;
      
      if (orderBy === '3' || orderBy === '4') {
        filename += '_CATEGORY';
      } else if (pdfType === 'team') {
        filename += '_TEAM';
      }
      
      filename += '.pdf';
      doc.save(filename);
      
      toast.success("PDF downloaded successfully!");
      onClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again with different settings.");
    }
  };

  // Get order by options based on PDF type
  const getOrderByOptions = () => {
    switch (pdfType) {
      case 'normal':
        return [
          { value: '1', label: 'Player Number' },
          { value: '2', label: 'Player Name' },
          { value: '3', label: 'Category' },
          { value: '4', label: 'Category-Namewise' }
        ];
      case 'category':
        return [
          { value: '1', label: 'Player Number' },
          { value: '2', label: 'Player Name' }
        ];
      case 'team':
        return [
          { value: '5', label: 'Sold Sequence' },
          { value: '6', label: 'Category' },
          { value: '7', label: 'Value' },
          { value: '8', label: 'Player Name' }
        ];
      case 'top':
      case 'player':
      case 'owners':
      default:
        return []; // These PDF types don't need ordering options
    }
  };

  // Check if order by options should be shown
  const shouldShowOrderBy = () => {
    return ['normal', 'category', 'team'].includes(pdfType);
  };

  // Get player filter status label
  const getPlayerFilterLabel = () => {
    switch (playerFilter) {
      case '1': return 'All';
      case '2': return 'Available';
      case '3': return 'Sold';
      case '4': return 'Unsold';
      case '5': return 'Unsold + Available';
      case '6': return 'Available + Unsold';
      default: return 'All';
    }
  };

  // Function to check if column selection should be shown (only for Type 1 layout)
  const shouldShowColumnSelection = () => {
    return ['normal', 'category', 'team'].includes(pdfType) && 
           pdfLayout === '1' && 
           orientation === 'portrait';
  };

  // Function to get per page player options based on column count
  const getPerPagePlayerOptions = () => {
    switch (columnCount) {
      case '2':
        return [
          { value: '12', label: 'First page-12/Rest-14' },
          { value: '14', label: 'First page-14/Rest-16' },
          { value: '16', label: 'First page-16/Rest-18' }
        ];
      case '3':
        return [
          { value: '24', label: 'First page-24/Rest-30' },
          { value: '27', label: 'First page-27/Rest-33' },
          { value: '30', label: 'First page-30/Rest-36' },
          { value: '33', label: 'First page-33/Rest-39' }
        ];
      default:
        return [
          { value: '12', label: 'First page-12/Rest-14' },
          { value: '14', label: 'First page-14/Rest-16' },
          { value: '16', label: 'First page-16/Rest-18' }
        ];
    }
  };

  // Reset column count when layout or orientation changes
  const handleLayoutChange = (layout) => {
    setPdfLayout(layout);
    if (layout !== '1') {
      setColumnCount('2');
      setPerPagePlayers('14');
    }
  };

  // Handle orientation change
  const handleOrientationChange = (newOrientation) => {
    setOrientation(newOrientation);
    if (newOrientation !== 'portrait') {
      setColumnCount('2');
      setPerPagePlayers('14');
    }
  };

  // Handle column count change
  const handleColumnCountChange = (value) => {
    setColumnCount(value);
    // Set default per page players based on column count
    if (value === '2') {
      setPerPagePlayers('14');
    } else if (value === '3') {
      setPerPagePlayers('24');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pdf-modal-overlay">
      <div className="pdf-modal">
        <div className="modal-header">
          <div className="modal-title">
            <FontAwesomeIcon icon={faFilePdf} className="pdf-icon" />
            <h2>Export to PDF</h2>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-content">
          <div className="export-panel">
            <div className="export-details">
              <h3>PDF Configuration</h3>
              <div className="export-summary">
                <div className="export-summary-row">
                  <span className="summary-label">Export type:</span>
                  <span className="summary-value">
                    {pdfType === 'normal' ? 'Normal PDF' : 
                     pdfType === 'category' ? 'Category-wise' : 
                     pdfType === 'team' ? 'Team-wise' :
                     pdfType === 'player' ? 'Player-wise' :
                     pdfType === 'top' ? 'Top players' :
                     pdfType === 'owners' ? 'Owners' :
                     'Normal PDF'}
                  </span>
                </div>
                <div className="export-summary-row">
                  <span className="summary-label">Status filter:</span>
                  <span className="summary-value">{getPlayerFilterLabel()}</span>
                </div>
                <div className="export-summary-row">
                  <span className="summary-label">Players:</span>
                  <span className="summary-value">{getPlayerCount()} players</span>
                </div>
                {orderBy && (
                  <div className="export-summary-row">
                    <span className="summary-label">Order by:</span>
                    <span className="summary-value">
                      {getOrderByOptions().find(option => option.value === orderBy)?.label || ''}
                    </span>
                  </div>
                )}
                <div className="export-summary-row">
                  <span className="summary-label">Layout:</span>
                  <span className="summary-value">Layout {pdfLayout}</span>
                </div>
                <div className="export-summary-row">
                  <span className="summary-label">Orientation:</span>
                  <span className="summary-value">{orientation}</span>
                </div>
              </div>
            </div>
            
            <div className="export-options">
              <div className="option-section">
                <h4>PDF Type</h4>
                <div className="option-cards">
                  <div 
                    className={`option-card ${pdfType === 'normal' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('normal')}
                  >
                    <div className="option-icon">
                      <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="option-details">
                      <h5>Normal PDF</h5>
                      <p>Export all players in order</p>
                    </div>
                    {pdfType === 'normal' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={`option-card ${pdfType === 'category' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('category')}
                  >
                    <div className="option-icon category-icon">
                      <FontAwesomeIcon icon={faLayerGroup} />
                    </div>
                    <div className="option-details">
                      <h5>Category-wise</h5>
                      <p>Group players by category</p>
                    </div>
                    {pdfType === 'category' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={`option-card ${pdfType === 'team' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('team')}
                  >
                    <div className="option-icon team-icon">
                      <FontAwesomeIcon icon={faShieldAlt} />
                    </div>
                    <div className="option-details">
                      <h5>Team-wise</h5>
                      <p>Group players by team</p>
                    </div>
                    {pdfType === 'team' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={`option-card ${pdfType === 'top' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('top')}
                  >
                    <div className="option-icon">
                      <FontAwesomeIcon icon={faStar} />
                    </div>
                    <div className="option-details">
                      <h5>Top Players</h5>
                      <p>Show highest valued players</p>
                    </div>
                    {pdfType === 'top' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={`option-card ${pdfType === 'player' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('player')}
                  >
                    <div className="option-icon">
                      <FontAwesomeIcon icon={faAddressCard} />
                    </div>
                    <div className="option-details">
                      <h5>Player-wise</h5>
                      <p>Individual player cards</p>
                    </div>
                    {pdfType === 'player' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={`option-card ${pdfType === 'owners' ? 'active' : ''}`}
                    onClick={() => handlePdfTypeChange('owners')}
                  >
                    <div className="option-icon">
                      <FontAwesomeIcon icon={faUserTie} />
                    </div>
                    <div className="option-details">
                      <h5>Owners</h5>
                      <p>Group by owner tags</p>
                    </div>
                    {pdfType === 'owners' && (
                      <div className="option-selected">
                        <FontAwesomeIcon icon={faCheck} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Top Players specific options */}
              {pdfType === 'top' && (
                <div className="option-section">
                  <h4>Top Players Options</h4>
                  <div className="filtering-options">
                    <div className="filter-column">
                      <label>Top Type</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={toppltype}
                          onChange={(e) => setToppltype(e.target.value)}
                        >
                          <option value="1">All</option>
                          <option value="2">Category wise</option>
                        </select>
                      </div>
                    </div>
                    <div className="filter-column">
                      <label>Top Number</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={topnum}
                          onChange={(e) => setTopnum(e.target.value)}
                        >
                          <option value="1">Top 1</option>
                          <option value="2">Top 2</option>
                          <option value="3">Top 3</option>
                          <option value="4">Top 4</option>
                          <option value="5">Top 5</option>
                          <option value="6">Top 6</option>
                          <option value="7">Top 7</option>
                          <option value="8">Top 8</option>
                          <option value="9">Top 9</option>
                          <option value="10">Top 10</option>
                          <option value="11">Top 11</option>
                          <option value="12">Top 12</option>
                        </select>
                      </div>
                    </div>
                    <div className="filter-column">
                      <label>Layout Type</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={toptype}
                          onChange={(e) => setToptype(e.target.value)}
                        >
                          <option value="0">Single page</option>
                          <option value="1">Multiple pages</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ignore options */}
                  <div className="filtering-options">
                    <div className="filter-column">
                      <label>Want to ignore?</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={ignoretype}
                          onChange={(e) => setIgnoretype(e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="retain">Retain</option>
                          <option value="cat">Category</option>
                          <option value="tag">Tag</option>
                          <option value="plno">Player Number</option>
                        </select>
                      </div>
                    </div>
                    
                    {ignoretype === 'cat' && (
                      <div className="filter-column">
                        <label>Select Category</label>
                        <div className="order-by-select">
                          <select
                            className="form-select"
                            value={ignorecat}
                            onChange={(e) => setIgnorecat(e.target.value)}
                          >
                            <option value="">Select category</option>
                            {categories && categories.map((cat) => (
                              <option key={cat.cid} value={cat.cid}>{cat.cname}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {ignoretype === 'tag' && (
                      <div className="filter-column">
                        <label>Select Tag</label>
                        <div className="order-by-select">
                          <select
                            className="form-select"
                            value={ignoretag}
                            onChange={(e) => setIgnoretag(e.target.value)}
                          >
                            <option value="">Select tag</option>
                            <option value="1">Owner</option>
                            <option value="2">Co-owner</option>
                            <option value="3">Captain</option>
                            <option value="4">Icon</option>
                            <option value="5">Retain</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {ignoretype === 'plno' && (
                    <div className="filter-column">
                      <label>Player Numbers (comma separated)</label>
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder="Enter Player numbers (e.g., 25,50,30)" 
                        value={ignoreplnos} 
                        onChange={(e) => setIgnoreplnos(e.target.value)} 
                      />
                    </div>
                  )}
                </div>
              )}
              
              {/* Owners specific options */}
              {pdfType === 'owners' && (
                <div className="option-section">
                  <h4>Owner Tags Selection</h4>
                  <div className="data-options">
                    <div className="data-group">
                      <div className="data-options-row">
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="ownertag"
                            className="check1"
                            checked={isownertagselect}
                            onChange={(e) => setIsownertagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="ownertag">
                            Owner
                          </label>
                        </div>
                        
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="co-owner"
                            className="check1"
                            checked={iscoownertagselect}
                            onChange={(e) => setIscoownertagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="co-owner">
                            Co-Owner
                          </label>
                        </div>
                        
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="captain"
                            className="check1"
                            checked={iscaptaintagselect}
                            onChange={(e) => setIscaptaintagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="captain">
                            Captain
                          </label>
                        </div>
                      </div>
                      
                      <div className="data-options-row">
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="vicecaptain"
                            className="check1"
                            checked={isvicecaptaintagselect}
                            onChange={(e) => setIsvicecaptaintagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="vicecaptain">
                            Vice Captain
                          </label>
                        </div>
                        
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="icon"
                            className="check1"
                            checked={isicontagselect}
                            onChange={(e) => setIsicontagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="icon">
                            Icon
                          </label>
                        </div>
                        
                        <div className="form-check">
                          <input 
                            type="checkbox"
                            id="retain"
                            className="check1"
                            checked={isretaintagselect}
                            onChange={(e) => setIsretaintagselect(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="retain">
                            Retain
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {shouldShowOrderBy() && (
                <div className="option-section">
                  <h4>Player Options</h4>
                  <div className="filtering-options">
                    <div className="filter-column">
                      <label>Player Status Filter</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={playerFilter}
                          onChange={(e) => setPlayerFilter(e.target.value)}
                          aria-label="Player status filter"
                        >
                          <option value="1">ALL</option>
                          <option value="2">Available</option>
                          <option value="3">Sold</option>
                          <option value="4">Unsold</option>
                          <option value="5">Unsold + Available</option>
                          <option value="6">Available + Unsold</option>
                        </select>
                      </div>
                    </div>
                    <div className="filter-column">
                      <label>Order By</label>
                      <div className="order-by-select">
                        <select
                          className="form-select"
                          value={orderBy}
                          onChange={(e) => setOrderBy(e.target.value)}
                          aria-label="Order by"
                        >
                          <option value="" disabled>Select order</option>
                          {getOrderByOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Layout Section - Only show for normal, category, team PDFs */}
              {['normal', 'category', 'team'].includes(pdfType) && (
                <div className="option-section layout-section">
                  <h4>Layout & Orientation</h4>
                  <div className="layout-options">
                    <div className="layout-column">
                      <div 
                        className={`layout-option ${pdfLayout === '1' ? 'active' : ''}`}
                        onClick={() => handleLayoutChange('1')}
                      >
                        <div className="layout-preview">
                          <img src={pdflayout1} alt="Grid Layout" className="layout-image" />
                        </div>
                        <div className="layout-label">
                          <span>Type 1 (Grid)</span>
                          {pdfLayout === '1' && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                        </div>
                      </div>
                      <div 
                        className={`layout-option ${pdfLayout === '2' ? 'active' : ''}`}
                        onClick={() => handleLayoutChange('2')}
                      >
                        <div className="layout-preview">
                          <img src={pdflayout2} alt="Circular Layout" className="layout-image" />
                        </div>
                        <div className="layout-label">
                          <span>Type 2 (Circular)</span>
                          {pdfLayout === '2' && <FontAwesomeIcon icon={faCheck} className="check-icon" />}
                        </div>
                      </div>
                    </div>
                  <div className="orientation-column">
                    <h5>Orientation</h5>
                    <div className="orientation-options">
                      <div className="orientation-option active locked">
                        <div className="orientation-preview portrait"></div>
                        <div className="orientation-label">
                          <span>Portrait</span>
                          <FontAwesomeIcon icon={faCheck} className="check-icon" />
                          <span className="locked-text">(Locked)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {shouldShowColumnSelection() && (
                  <div className="column-settings-section">
                    <div className="column-options-wrapper">
                      <div className="column-option">
                        <h5>Column Count</h5>
                        <select
                          className="form-select"
                          value={columnCount}
                          onChange={(e) => handleColumnCountChange(e.target.value)}
                        >
                          <option value="2">Two Columns</option>
                          <option value="3">Three Columns</option>
                        </select>
                      </div>

                      <div className="column-option">
                        <h5>Players Per Page</h5>
                        <select
                          className="form-select"
                          value={perPagePlayers}
                          onChange={(e) => setPerPagePlayers(e.target.value)}
                        >
                          {getPerPagePlayerOptions().map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          </div>

          <div className="data-selection-section">
            <h4>Data Selection</h4>
            <div className="data-options">
              {/* Essential Information Group */}
              <div className="data-group">
                <h5>Essential Information</h5>
                <div className="data-options-row">
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="plno"
                      className="check1"
                      checked={isPlNoShow}
                      onChange={(e) => setIsPlNoShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="plno">
                      Player Number
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="category"
                      className="check1"
                      checked={isCatShow}
                      onChange={(e) => setIsCatShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="category">
                      Category
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="points"
                      className="check1"
                      checked={isPointsShow}
                      onChange={(e) => setIsPointsShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="points">
                      Points
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="value"
                      className="check1"
                      checked={isValShow}
                      onChange={(e) => setIsValShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="value">
                      Value
                    </label>
                  </div>
                </div>
              </div>

              {/* Specifications Group */}
              <div className="data-group">
                <h5>Specifications</h5>
                <div className="data-options-row">
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="spec1"
                      className="check1"
                      checked={isSpec1}
                      onChange={(e) => setIsSpec1(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="spec1">
                      Specification 1
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="spec2"
                      className="check1"
                      checked={isSpec2}
                      onChange={(e) => setIsSpec2(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="spec2">
                      Specification 2
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="spec3"
                      className="check1"
                      checked={isSpec3}
                      onChange={(e) => setIsSpec3(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="spec3">
                      Specification 3
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="detail"
                      className="check1"
                      checked={isDetailShow}
                      onChange={(e) => setIsDetailShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="detail">
                      Details
                    </label>
                  </div>
                </div>
              </div>

              {/* Additional Information Group */}
              <div className="data-group">
                <h5>Additional Information</h5>
                <div className="data-options-row">
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="mobile"
                      className="check1"
                      checked={isMobNoShow}
                      onChange={(e) => setIsMobNoShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="mobile">
                      Mobile Number
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="tshirt"
                      className="check1"
                      checked={isTshirtShow}
                      onChange={(e) => setIsTshirtShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="tshirt">
                      Tshirt Size
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="baseprice"
                      className="check1"
                      checked={isBasePriceShow}
                      onChange={(e) => setIsBasePriceShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="baseprice">
                      Base Price
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="owner"
                      className="check1"
                      checked={isOwnerShow}
                      onChange={(e) => setIsOwnerShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="owner">
                      Owner
                    </label>
                  </div>
                </div>
              </div>

              {/* Display Options - removed heading */}
              <div className="data-group no-header">
                <div className="data-options-row">
                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="sponsor"
                      className="check1"
                      checked={isSpnShow}
                      onChange={(e) => setIsSpnShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="sponsor">
                      Show Sponsor
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="nocat"
                      className="check1"
                      checked={isNoCatShow}
                      onChange={(e) => setIsNoCatShow(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="nocat">
                      Show No Category
                    </label>
                  </div>

                  <div className="form-check">
                    <input 
                      type="checkbox"
                      id="whitebg"
                      className="check1"
                      checked={ispdfwhitebg === '1'}
                      onChange={(e) => {
                        const value = e.target.checked ? '1' : '0';
                        setIspdfwhitebg(value);
                        localStorage.setItem('ispdfwhitebg', value);
                        // Set font color based on background: white bg = dark text, dark bg = white text
                        const fontValue = value === '1' ? '1' : '0';
                        setFontcolor(fontValue);
                        localStorage.setItem('pdffont', fontValue);
                      }}
                    />
                    <label className="form-check-label" htmlFor="whitebg">
                      White Background
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-download" onClick={handleGeneratePdf}>
            <FontAwesomeIcon icon={faDownload} className="download-icon" />
            Download PDF
          </button>
        </div>
      </div>
      
      {/* Hidden images for base64 conversion (similar to Players.jsx lines 5245-5291) */}
      {isOpen && (
        <div style={{ display: 'none' }}>
          {auctionLogo && (
            <img
              ref={(img) => {
                if (img) {
                  aucImageRefs.current['0'] = img;
                }
              }}
              onLoad={handleaucimgload('0')}
              src={auctionLogo + '?c'}
              alt="auction logo"
              crossOrigin="anonymous"
            />
          )}
          {sponsorLogo && (
            <img
              ref={(img) => {
                if (img) {
                  aucImageRefs.current['1'] = img;
                }
              }}
              onLoad={handleaucimgload('1')}
              src={sponsorLogo + '?c'}
              alt="sponsor logo"
              crossOrigin="anonymous"
            />
          )}
          {backgroundImage && (
            <img
              ref={(img) => {
                if (img) {
                  aucImageRefs.current['2'] = img;
                }
              }}
              onLoad={handleaucimgload('2')}
              src={backgroundImage + '?c'}
              alt="background image"
              crossOrigin="anonymous"
            />
          )}
          {/* Load team logos */}
          {teams && teams.map((team) => {
            if (!team.logo) return null;
            const teamLogoUrl = teamPath ? `${teamPath}${team.logo}?c` : `${team.logo}?c`;
            console.log(`Loading team logo for ${team.name}: ${teamLogoUrl}`);
            return (
              <img
                key={team.tid}
                ref={(img) => {
                  if (img) {
                    console.log(`Setting ref for team ${team.name} (ID: ${team.tid})`);
                    aucImageRefs.current[team.tid] = img;
                  }
                }}
                onLoad={() => {
                  console.log(`Team logo loaded for ${team.name} (ID: ${team.tid})`);
                  handleaucimgload(team.tid)();
                }}
                src={teamLogoUrl}
                alt={`team ${team.name} logo`}
                crossOrigin="anonymous"
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PDFExportModal;
